/* ===================================================================
   garage-modal-v2.js — Controller for the redesigned Garage Modal
   Dependencies: global-library.js (getSearchTerms, setSearchTerms,
     moveVehicleToFront, generateCollectionHandle, setFitmentObject,
     handleVinDecode, fetchBumpersLicenseToVin, cleanLicensePlate,
     handleVinChange, handleLicenseChange, handleStateSelectChange,
     logGarageEventToSheets, updateSearchTerms)
   =================================================================== */

/* Controller gate — tells legacy garage-script to yield polling & nav */
window.__GARAGE_CONTROLLER__ = "v2";

(function () {
	"use strict";

	/* ---- Selectors ---- */
	const SEL = {
		overlay: "[data-gv2-overlay]",
		modal: "[data-gv2-modal]",
		close: "[data-gv2-close]",
		left: "[data-gv2-left]",
		right: "[data-gv2-right]",
		// Views
		garageView: '[data-gv2-view="garage"]',
		addView: '[data-gv2-view="add"]',
		vehicleList: "[data-gv2-vehicle-list]",
		addVehicleBtn: "[data-gv2-add-vehicle]",
		backBtn: "[data-gv2-back-to-garage]",
		formTitle: "[data-gv2-form-title]",
		// Tabs
		tabs: "[data-gv2-tabs]",
		tab: "[data-gv2-tab]",
		panel: "[data-gv2-panel]",
		// Manual
		manualSearch: "[data-gv2-manual-search]",
		// VIN
		vinInput: "[data-gv2-vin-input]",
		vinSubmit: "[data-gv2-vin-submit]",
		vinError: "[data-gv2-vin-error]",
		// License
		licenseState: "[data-gv2-license-state]",
		licensePlate: "[data-gv2-license-plate]",
		licenseSubmit: "[data-gv2-license-submit]",
		licenseError: "[data-gv2-license-error]",
		// Preview
		preview: "[data-gv2-preview]",
		previewImage: "[data-gv2-vehicle-image]",
		spinner: "[data-gv2-spinner]",
		previewTitle: "[data-gv2-preview-title]",
		previewVin: "[data-gv2-preview-vin]",
		previewPaint: "[data-gv2-preview-paint]",
		browseCatalog: "[data-gv2-browse-catalog]",
		brandLogos: "[data-gv2-brand-logos]",
		// Empty state
		emptyState: "[data-gv2-empty-state]",
		addVehicleEmpty: "[data-gv2-add-vehicle-empty]",
	};

	/* ---- State ---- */
	let isOpen = false;
	let editState = null; // { ymm, index } when editing
	let currentActiveVehicle = null; // the vehicle object being previewed
	let imageCache = {}; // handle → image_url
	let bumperdotcommake = false;
	let _vehicleAtOpenTime = null; // snapshot for PDP reload-on-change
	let _nativeYmmInstance = null; // Phase 2: native YMM selector instance

	/* ---- Timer IDs (cleared on close) ---- */
	let _manualBtnTimer = null;
	let _fitmentPollTimer = null;
	let _autoSubmitTimer = null;
	let _pendingSubmodelFocus = false;
	let _manualDraftRequiresConfirm = false;
	let _fitmentResultScrollToken = 0;
	let _fitmentDraftPreviewSeq = 0;
	let _fitmentDraftPreviewState = "idle";
	let _submodelListDialog = null;
	let _submodelListTrigger = null;
	let _submodelListKeydownHandler = null;
	const _rowFitmentResolveCache = new Map();
	const _rowFitmentYearDataCache = new Map();

	/* ---- DOM refs (set in init) ---- */
	let $overlay,
		$modal,
		$garageView,
		$addView,
		$vehicleList,
		$backBtn,
		$formTitle;
	let $manualSearch, $nativeYmmSlot;
	let $vinInput, $vinSubmit, $vinError;
	let $licenseState, $licensePlate, $licenseSubmit, $licenseError;
	let $previewImage,
		$preview,
		$spinner,
		$previewTitle,
		$previewVin,
		$previewPaint,
		$browseCatalog,
		$brandLogos,
		$emptyState;

	const cfg = () => window.__gv2Config || {};
	const i18n = (key) => (cfg().i18n || {})[key] || key;
	const FOCUSABLE_SELECTOR = [
		'a[href]',
		'button:not([disabled])',
		'input:not([disabled]):not([type="hidden"])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'[tabindex]:not([tabindex="-1"])',
	].join(",");

	function isTouchupFinderOpen() {
		return Boolean(document.querySelector("[data-tuf-overlay].tuf-open"));
	}

	function returnToTouchupFinder(vehicle) {
		if (!isTouchupFinderOpen()) return false;
		const activeVehicle = vehicle || getTerms()[0] || null;
		if (activeVehicle) syncActiveVehicle(activeVehicle);
		close();
		return true;
	}

	function normalizeFitmentText(value) {
		return String(value || "")
			.replace(/\s+/g, " ")
			.trim()
			.toLowerCase();
	}

	function normalizeFitmentIdentity(value) {
		return normalizeFitmentText(value).replace(/[^a-z0-9]/g, "");
	}

	function fitmentValueMatches(rowValue, entryValue) {
		const entryIdentity = normalizeFitmentIdentity(entryValue);
		if (!entryIdentity) return false;
		const rowIdentity = normalizeFitmentIdentity(rowValue);
		if (rowIdentity === entryIdentity) return true;
		return String(rowValue || "")
			.split(/\s*(?:\/|,|&|\band\b)\s*/i)
			.some(function (part) {
				return normalizeFitmentIdentity(part) === entryIdentity;
			});
	}

	function cleanFitmentYear(value) {
		const year = String(value || "").trim();
		return /^\d{4}$/.test(year) ? year : "";
	}

	function parseFitmentYears(value) {
		const text = String(value || "");
		const years = [];
		function addYear(year) {
			const cleanYear = cleanFitmentYear(year);
			if (cleanYear && years.indexOf(cleanYear) === -1) years.push(cleanYear);
		}
		text.replace(
			/\b((?:19|20)\d{2})\b\s*(?:-|\u2013|\u2014|\bto\b)\s*\b((?:19|20)\d{2})\b/gi,
			function (_match, startYear, endYear) {
				const start = Number(startYear);
				const end = Number(endYear);
				if (end >= start && end - start <= 40) {
					for (let year = start; year <= end; year += 1) {
						addYear(String(year));
					}
				} else {
					addYear(startYear);
					addYear(endYear);
				}
				return _match;
			},
		);
		text.replace(/\b((?:19|20)\d{2})\b/g, function (_match, year) {
			addYear(year);
			return _match;
		});
		return years.sort(function (a, b) {
			return Number(b) - Number(a);
		});
	}

	function uniqueFitmentValues(values) {
		const seen = new Set();
		const out = [];
		values.forEach(function (value) {
			const cleanValue = String(value || "").replace(/\s+/g, " ").trim();
			const key = normalizeFitmentText(cleanValue);
			if (!cleanValue || seen.has(key)) return;
			seen.add(key);
			out.push(cleanValue);
		});
		return out;
	}

	function formatSubmodelList(submodels) {
		const uniqueSubmodels = uniqueFitmentValues(submodels);
		if (!uniqueSubmodels.length) return "";
		if (uniqueSubmodels.length <= 4) return uniqueSubmodels.join(", ");
		return uniqueSubmodels.slice(0, 4).join(", ") + " +" + (uniqueSubmodels.length - 4) + " more";
	}

	function closeSubmodelListDialog() {
		if (_submodelListKeydownHandler) {
			document.removeEventListener("keydown", _submodelListKeydownHandler);
			_submodelListKeydownHandler = null;
		}
		if (_submodelListDialog) {
			_submodelListDialog.remove();
			_submodelListDialog = null;
		}
		if (_submodelListTrigger && typeof _submodelListTrigger.focus === "function") {
			_submodelListTrigger.focus();
		}
		_submodelListTrigger = null;
	}

	function openSubmodelListDialog(submodels, trigger) {
		const uniqueSubmodels = uniqueFitmentValues(submodels);
		if (!uniqueSubmodels.length) return;
		closeSubmodelListDialog();
		_submodelListTrigger = trigger || null;

		const overlay = document.createElement("div");
		overlay.className = "partify-fitment-data__submodel-dialog-overlay";

		const dialog = document.createElement("div");
		dialog.className = "partify-fitment-data__submodel-dialog";
		dialog.setAttribute("role", "dialog");
		dialog.setAttribute("aria-modal", "true");

		const titleId = "partify-submodel-dialog-title";
		const title = document.createElement("h3");
		title.id = titleId;
		title.className = "partify-fitment-data__submodel-dialog-title";
		title.textContent = "Available submodels";
		dialog.setAttribute("aria-labelledby", titleId);

		const closeButton = document.createElement("button");
		closeButton.type = "button";
		closeButton.className = "partify-fitment-data__submodel-dialog-close";
		closeButton.setAttribute("aria-label", "Close submodel list");
		closeButton.textContent = "x";
		closeButton.addEventListener("click", closeSubmodelListDialog);

		const list = document.createElement("ul");
		list.className = "partify-fitment-data__submodel-dialog-list";
		uniqueSubmodels.forEach(function (submodel) {
			const item = document.createElement("li");
			item.textContent = submodel;
			list.appendChild(item);
		});

		dialog.appendChild(closeButton);
		dialog.appendChild(title);
		dialog.appendChild(list);
		overlay.appendChild(dialog);
		overlay.addEventListener("click", function (event) {
			if (event.target === overlay) closeSubmodelListDialog();
		});

		_submodelListKeydownHandler = function (event) {
			if (event.key === "Escape") closeSubmodelListDialog();
		};
		document.addEventListener("keydown", _submodelListKeydownHandler);
		document.body.appendChild(overlay);
		_submodelListDialog = overlay;
		closeButton.focus();
	}

	function renderSubmodelCellSummary(draft, cell) {
		if (!cell) return;
		const submodels = uniqueFitmentValues((draft && draft.submodels) || []);
		const fullLabel = submodels.join(", ");
		const hiddenCount = Math.max(submodels.length - 4, 0);
		const isExpandable = hiddenCount > 0;

		cell.textContent = "";
		if (fullLabel) {
			cell.title = fullLabel;
		} else {
			cell.removeAttribute("title");
		}

		if (!submodels.length) return;

		if (!isExpandable) {
			cell.textContent = submodels.join(", ");
			return;
		}

		const wrap = document.createElement("div");
		wrap.className = "partify-fitment-data__submodel-summary";

		const text = document.createElement("span");
		text.className = "partify-fitment-data__submodel-text";
		text.textContent = submodels.slice(0, 4).join(", ") + " ";

		const moreButton = document.createElement("button");
		moreButton.type = "button";
		moreButton.className = "partify-fitment-data__submodel-more";
		moreButton.textContent = "+" + hiddenCount + " more";
		moreButton.setAttribute("aria-haspopup", "dialog");
		moreButton.setAttribute(
			"aria-label",
			"Show all submodels",
		);
		moreButton.addEventListener("click", function (event) {
			event.preventDefault();
			event.stopPropagation();
			openSubmodelListDialog(submodels, moreButton);
		});

		wrap.appendChild(text);
		wrap.appendChild(moreButton);
		cell.appendChild(wrap);
	}

	function getRowFitmentProductCacheKey() {
		const collections = Array.isArray(window.__PARTIFY_PRODUCT_COLLECTIONS)
			? window.__PARTIFY_PRODUCT_COLLECTIONS.join(",")
			: "";
		return [
			window.__PARTIFY_PRODUCT_TYPE || "",
			window.__PARTIFY_PRODUCT_TITLE || "",
			collections,
		].join("|");
	}

	function buildRowFitmentCandidate(vehicle) {
		const candidate = {
			year: cleanFitmentYear(vehicle && vehicle.year),
			make: String((vehicle && vehicle.make) || "").trim(),
			model: String((vehicle && vehicle.model) || "").trim(),
			submodel: String((vehicle && vehicle.submodel) || "").trim(),
			engine: String((vehicle && vehicle.engine) || "").trim(),
		};
		candidate.ymm = [
			candidate.year,
			candidate.make,
			candidate.model,
			candidate.submodel,
			candidate.engine,
		]
			.filter(Boolean)
			.join(" ");
		return candidate;
	}

	function rowFitmentCandidatePasses(vehicle) {
		const resolver = window.PartifyFitment;
		if (!resolver || typeof resolver.resolveVehicle !== "function") return true;

		const candidate = buildRowFitmentCandidate(vehicle);
		if (!candidate.year || !candidate.make || !candidate.model) return true;
		const cacheKey = [
			getRowFitmentProductCacheKey(),
			candidate.year,
			normalizeFitmentText(candidate.make),
			normalizeFitmentText(candidate.model),
			normalizeFitmentText(candidate.submodel),
			normalizeFitmentText(candidate.engine),
		].join("|");
		if (_rowFitmentResolveCache.has(cacheKey)) {
			return _rowFitmentResolveCache.get(cacheKey);
		}

		let passes = true;
		try {
			const result = resolver.resolveVehicle(candidate);
			passes = !(result && result.state === "fail");
		} catch (_) {
			passes = true;
		}
		_rowFitmentResolveCache.set(cacheKey, passes);
		return passes;
	}

	function getRowFitmentYearDataUrl(year) {
		const proxy = window.__PARTIFY_YMM_PROXY__ || "";
		if (proxy) return proxy + "/ymm-data/" + year;
		const base = window.__PARTIFY_YMM_BASE__ || "";
		if (base) return base + "ymm-" + year + ".json";
		return "/ymm-data/" + year;
	}

	function loadRowFitmentYearData(year) {
		const cleanYear = cleanFitmentYear(year);
		if (!cleanYear || typeof fetch !== "function") return Promise.resolve(null);
		if (_rowFitmentYearDataCache.has(cleanYear)) {
			return _rowFitmentYearDataCache.get(cleanYear);
		}
		const promise = fetch(getRowFitmentYearDataUrl(cleanYear))
			.then(function (response) {
				if (!response.ok) return null;
				return response.json();
			})
			.catch(function () {
				return null;
			});
		_rowFitmentYearDataCache.set(cleanYear, promise);
		return promise;
	}

	function getDraftRulesForSubmodel(draft, submodel) {
		const submodelKey = normalizeFitmentText(submodel);
		const allowedVehicles = Array.isArray(draft && draft.allowedVehicles)
			? draft.allowedVehicles
			: [];
		const rules = allowedVehicles.filter(function (vehicle) {
			return normalizeFitmentText(vehicle.submodel) === submodelKey;
		});
		if (rules.length) return rules;
		const allowedYears = Array.isArray(draft && draft.allowedYears)
			? draft.allowedYears
			: [];
		return allowedYears.map(function (year) {
			return {
				year: year,
				make: draft.make,
				model: draft.model,
				submodel: submodel,
				engine: "",
			};
		});
	}

	function getScopedRuleEngines(rules) {
		return uniqueFitmentValues(
			rules.map(function (rule) {
				return rule.engine;
			}),
		);
	}

	function updateDraftSubmodelSummary(draft, submodels) {
		const filteredSubmodels = uniqueFitmentValues(submodels);
		draft.submodels = filteredSubmodels;
		draft.submodel = filteredSubmodels.length === 1 ? filteredSubmodels[0] : "";
		draft.submodelLabel = formatSubmodelList(filteredSubmodels);
		draft.fitmentLabel = [
			compactYears(draft.allowedYears || []),
			draft.make,
			draft.model,
			draft.submodelLabel,
		]
			.filter(Boolean)
			.join(" ");
		return draft.submodelLabel;
	}

	function rowSubmodelHasAllowedPath(draft, submodel) {
		const ymm = window.PartifyYMM;
		if (!ymm || typeof ymm.filterAllowedSubmodels !== "function") {
			return Promise.resolve(true);
		}
		const rules = getDraftRulesForSubmodel(draft, submodel);
		const years = uniqueFitmentValues(
			rules.map(function (rule) {
				return rule.year;
			}),
		);
		if (!years.length) return Promise.resolve(true);

		return years
			.reduce(function (chain, year) {
				return chain.then(function (alreadyAllowed) {
					if (alreadyAllowed) return true;
					const yearRules = rules.filter(function (rule) {
						return rule.year === year;
					});
					const baseVehicle = buildRowFitmentCandidate({
						year: year,
						make: draft.make,
						model: draft.model,
						submodel: submodel,
						engine: "",
					});
					const scopedEngines = getScopedRuleEngines(yearRules);
					function filterWithModalCandidates(yearData) {
						return ymm
							.filterAllowedSubmodels({
								year: year,
								make: baseVehicle.make,
								model: baseVehicle.model,
								submodels: [submodel],
								allowedVehicles: yearRules,
								vehicles:
									yearData && Array.isArray(yearData.vehicles)
										? yearData.vehicles
										: [],
								vehicleCandidateFilter: rowFitmentCandidatePasses,
							})
							.some(function (candidateSubmodel) {
								return (
									normalizeFitmentText(candidateSubmodel) ===
									normalizeFitmentText(submodel)
								);
							});
					}
					if (scopedEngines.length) return filterWithModalCandidates(null);
					return loadRowFitmentYearData(year).then(function (yearData) {
						if (!yearData || !Array.isArray(yearData.vehicles)) return true;
						return filterWithModalCandidates(yearData);
					});
				});
			}, Promise.resolve(false))
			.catch(function () {
				return true;
			});
	}

	function filterDraftSubmodelsWithModalCandidates(draft) {
		const submodels = uniqueFitmentValues((draft && draft.submodels) || []);
		if (!submodels.length) return Promise.resolve(submodels);
		const filtered = [];
		return submodels
			.reduce(function (chain, submodel) {
				return chain.then(function () {
					return rowSubmodelHasAllowedPath(draft, submodel).then(function (
						isAllowed,
					) {
						if (isAllowed) filtered.push(submodel);
					});
				});
			}, Promise.resolve())
			.then(function () {
				return filtered;
			})
			.catch(function () {
				return submodels;
			});
	}

	function parseFitmentVehicleEntries(root) {
		if (!root) return [];
		return Array.from(
			root.querySelectorAll("[data-partify-fitment-vehicle]"),
		)
			.map((entry) => ({
				source: String(entry.getAttribute("data-source") || "").trim(),
				sourceKey: String(entry.getAttribute("data-source-key") || "").trim(),
				year: cleanFitmentYear(entry.getAttribute("data-year")),
				make: String(entry.getAttribute("data-make") || "").trim(),
				model: String(entry.getAttribute("data-model") || "").trim(),
				submodel: String(entry.getAttribute("data-submodel") || "").trim(),
				engine: String(entry.getAttribute("data-engine") || "").trim(),
			}))
			.filter((entry) => entry.year && entry.make && entry.model);
	}

	function compactYears(years) {
		const sortedYears = Array.from(new Set(years))
			.filter((year) => /^\d{4}$/.test(year))
			.sort((a, b) => Number(a) - Number(b));
		if (!sortedYears.length) return "";
		if (sortedYears.length === 1) return sortedYears[0];
		const isContiguous = sortedYears.every((year, index) => {
			if (index === 0) return true;
			return Number(year) === Number(sortedYears[index - 1]) + 1;
		});
		if (isContiguous) {
			return sortedYears[0] + "-" + sortedYears[sortedYears.length - 1];
		}
		return sortedYears.join(", ");
	}

	function buildFitmentRowKey(make, model, years) {
		return [
			normalizeFitmentText(make),
			normalizeFitmentText(model),
			compactYears(years),
		].join("|");
	}

	function buildScopeFromEntries(entries, rowData) {
		const records = Array.isArray(entries) ? entries : [];
		const firstRecord = records[0] || {};
		const make = String((rowData && rowData.make) || firstRecord.make || "").trim();
		const model = String(
			(rowData && rowData.model) || firstRecord.model || "",
		).trim();
		const allowedYears = uniqueFitmentValues(
			records.length
				? records.map(function (entry) {
						return entry.year;
					})
				: (rowData && rowData.years) || [],
		).sort(function (a, b) {
			return Number(b) - Number(a);
		});
		if (!make || !model || !allowedYears.length) return null;

		const submodels = uniqueFitmentValues(
			records.map(function (entry) {
				return entry.submodel;
			}),
		);
		const submodelLabel = formatSubmodelList(submodels);
		const allowedVehicles = records.length
			? records.map(function (entry) {
					return {
						year: entry.year,
						make: entry.make || make,
						model: entry.model || model,
						submodel: entry.submodel || "",
						engine: entry.engine || "",
					};
				})
			: allowedYears.map(function (year) {
					return {
						year: year,
						make: make,
						model: model,
						submodel: "",
						engine: "",
					};
				});
		const yearLabel = compactYears(allowedYears);
		return {
			rowKey: buildFitmentRowKey(make, model, allowedYears),
			year: "",
			make: make,
			model: model,
			submodel: submodels.length === 1 ? submodels[0] : "",
			submodels: submodels,
			submodelLabel: submodelLabel,
			allowedYears: allowedYears,
			allowedVehicles: allowedVehicles,
			sourceKeys: uniqueFitmentValues(
				records.map(function (entry) {
					return entry.sourceKey;
				}),
			),
			fitmentLabel: [yearLabel, make, model, submodelLabel]
				.filter(Boolean)
				.join(" "),
			requireExplicitSelections: true,
		};
	}

	function buildStructuredFitmentScopes(entries) {
		const byVehicle = new Map();
		entries.forEach((entry) => {
			const key =
				normalizeFitmentText(entry.make) + "|" + normalizeFitmentText(entry.model);
			if (!byVehicle.has(key)) {
				byVehicle.set(key, {
					make: entry.make,
					model: entry.model,
					years: new Set(),
					records: [],
					sourceKeys: new Set(),
				});
			}
			const vehicle = byVehicle.get(key);
			vehicle.years.add(entry.year);
			vehicle.records.push(entry);
			if (entry.sourceKey) vehicle.sourceKeys.add(entry.sourceKey);
		});
		return Array.from(byVehicle.values())
			.map((vehicle) => {
				return buildScopeFromEntries(vehicle.records, {
					make: vehicle.make,
					model: vehicle.model,
					years: Array.from(vehicle.years),
				});
			})
			.filter(hasFitmentVehicleDraft);
	}

	function getFitmentDataRows(table) {
		const rows = Array.from(table.querySelectorAll("tr")).filter(
			(row) => row.querySelector("td"),
		);
		return rows;
	}

	function getFitmentHeaderLabels(header) {
		if (!header) return [];
		return Array.from(header.children).map(function (cell) {
			return String(cell.textContent || "").replace(/\s+/g, " ").trim();
		});
	}

	function findFitmentColumnIndex(labels, acceptedLabels, fallbackIndex) {
		const accepted = acceptedLabels.map(normalizeFitmentIdentity);
		const index = labels.findIndex(function (label) {
			return accepted.indexOf(normalizeFitmentIdentity(label)) >= 0;
		});
		return index >= 0 ? index : fallbackIndex;
	}

	function readVisibleFitmentRow(row, header) {
		const labels = getFitmentHeaderLabels(header);
		const cells = Array.from(row.children);
		const makeIndex = findFitmentColumnIndex(labels, ["Make"], 0);
		const modelIndex = findFitmentColumnIndex(labels, ["Model"], 1);
		const yearIndex = findFitmentColumnIndex(labels, ["Year"], 2);
		const make = String(cells[makeIndex]?.textContent || "")
			.replace(/\s+/g, " ")
			.trim();
		const model = String(cells[modelIndex]?.textContent || "")
			.replace(/\s+/g, " ")
			.trim();
		const yearText = String(cells[yearIndex]?.textContent || "")
			.replace(/\s+/g, " ")
			.trim();
		const years = parseFitmentYears(yearText);
		if (!make || !model || !years.length) return null;
		return {
			make: make,
			model: model,
			years: years,
		};
	}

	function getStructuredEntriesForRow(rowData, entries) {
		if (!rowData) return [];
		return entries.filter(function (entry) {
			return (
				fitmentValueMatches(rowData.make, entry.make) &&
				fitmentValueMatches(rowData.model, entry.model) &&
				rowData.years.indexOf(entry.year) >= 0
			);
		});
	}

	function buildFitmentRowDraft(row, header, entries, fallbackDraft) {
		const rowData = readVisibleFitmentRow(row, header);
		const matchedEntries = getStructuredEntriesForRow(rowData, entries);
		const matchedDraft = buildScopeFromEntries(matchedEntries, rowData);
		if (matchedDraft) return matchedDraft;
		if (hasFitmentVehicleDraft(fallbackDraft)) return fallbackDraft;
		return buildScopeFromEntries([], rowData);
	}

	function hasFitmentVehicleDraft(draft) {
		return Boolean(
			draft &&
				draft.make &&
				draft.model &&
				Array.isArray(draft.allowedYears) &&
				draft.allowedYears.some((year) => /^\d{4}$/.test(String(year || ""))),
		);
	}

	function insertFitmentCell(row, index, cell) {
		const beforeCell = row.children[index] || null;
		row.insertBefore(cell, beforeCell);
	}

	function setFitmentCellLabels(tableInfo) {
		if (!tableInfo.header) return;
		const labels = getFitmentHeaderLabels(tableInfo.header);
		tableInfo.rows.forEach(function (row) {
			Array.from(row.children).forEach(function (cell, index) {
				if (cell.dataset.fitmentLabel) return;
				const label = labels[index];
				if (label) cell.dataset.fitmentLabel = label;
			});
		});
	}

	function refreshSubmodelCellWithResolver(draft, cell) {
		if (!draft || !cell || !draft.submodels || !draft.submodels.length) return;
		filterDraftSubmodelsWithModalCandidates(draft).then(function (
			filteredSubmodels,
		) {
			updateDraftSubmodelSummary(draft, filteredSubmodels);
			renderSubmodelCellSummary(draft, cell);
			const actionButton = cell
				.closest("tr")
				?.querySelector(".partify-fitment-data__vehicle-action");
			if (actionButton) {
				actionButton.setAttribute("aria-label", getFitmentActionAriaLabel(draft));
			}
		});
	}

	function getFitmentActionAriaLabel(draft) {
		return (
			"Add " +
			[draft.fitmentLabel || "", draft.make, draft.model]
				.filter(Boolean)
				.join(" ") +
			" to garage"
		);
	}

	function addFitmentActionColumns(section, entries, scopes) {
		const tables = Array.from(section.querySelectorAll("table")).filter(
			(table) => table.dataset.partifyFitmentActions !== "ready",
		);
		if (!tables.length || !scopes.length) return;

		let scopeIndex = 0;
		const tableRows = tables.map((table) => {
			const header = Array.from(table.querySelectorAll("tr")).find((row) =>
				row.querySelector("th"),
			);
			const labels = getFitmentHeaderLabels(header);
			const rows = getFitmentDataRows(table);
			const drafts = rows.map((row) => {
				const draft = buildFitmentRowDraft(
					row,
					header,
					entries,
					scopes[scopeIndex],
				);
				scopeIndex += 1;
				return draft;
			});
			return {
				table: table,
				header: header,
				hadSubmodelHeader:
					findFitmentColumnIndex(labels, ["Submodel", "Sub Model"], -1) >=
					0,
				rows: rows,
				drafts: drafts,
			};
		});
		const totalRows = tableRows.reduce(
			(count, tableInfo) => count + tableInfo.rows.length,
			0,
		);

		const hasActionableDraft = tableRows.some(function (tableInfo) {
			return tableInfo.drafts.some(hasFitmentVehicleDraft);
		});
		const shouldShowSubmodel = tableRows.some(function (tableInfo) {
			return tableInfo.drafts.some(function (draft) {
				return hasFitmentVehicleDraft(draft) && !!draft.submodelLabel;
			});
		});

		if (!totalRows || !hasActionableDraft) return;

		tableRows.forEach((tableInfo) => {
			if (tableInfo.header) {
				const labels = getFitmentHeaderLabels(tableInfo.header);
				if (shouldShowSubmodel && !tableInfo.hadSubmodelHeader) {
					const yearIndex = findFitmentColumnIndex(labels, ["Year"], 2);
					const submodelHeader = document.createElement("th");
					submodelHeader.textContent = "Submodel";
					insertFitmentCell(tableInfo.header, yearIndex + 1, submodelHeader);
				}
				const headerCell = document.createElement("th");
				headerCell.textContent = "Action";
				headerCell.dataset.partifyFitmentActionHeader = "true";
				tableInfo.header.appendChild(headerCell);
			}

			tableInfo.rows.forEach((row, index) => {
				const draft = tableInfo.drafts[index];
				if (shouldShowSubmodel) {
					const labels = getFitmentHeaderLabels(tableInfo.header);
					const submodelIndex = findFitmentColumnIndex(
						labels,
						["Submodel", "Sub Model"],
						3,
					);
					let submodelCell =
						tableInfo.hadSubmodelHeader &&
						normalizeFitmentIdentity(labels[submodelIndex]) === "submodel"
							? row.children[submodelIndex]
							: null;
					if (!submodelCell) {
						submodelCell = document.createElement("td");
						insertFitmentCell(row, submodelIndex, submodelCell);
					}
					submodelCell.dataset.fitmentLabel = "Submodel";
					if (draft && draft.submodels && draft.submodels.length) {
						renderSubmodelCellSummary(draft, submodelCell);
					}
					refreshSubmodelCellWithResolver(draft, submodelCell);
				}
				const actionCell = document.createElement("td");
				actionCell.className = "partify-fitment-data__action-cell";
				actionCell.dataset.fitmentLabel = "Action";

				if (hasFitmentVehicleDraft(draft)) {
					const button = document.createElement("button");
					button.type = "button";
					button.className = "partify-fitment-data__vehicle-action";
					button.textContent = "Add Vehicle";
					button.dataset.fitmentRowKey = draft.rowKey || "";
					button.setAttribute("aria-label", getFitmentActionAriaLabel(draft));
					button.addEventListener("click", function () {
						openWithVehicleDraft(draft);
					});
					actionCell.appendChild(button);
				}

				row.appendChild(actionCell);
			});
			setFitmentCellLabels(tableInfo);
			tableInfo.table.dataset.partifyFitmentActions = "ready";
		});
	}

	function enhanceFitmentVehicleActions() {
		if (!$overlay) return;
		document.querySelectorAll(".partify-fitment-data").forEach((section) => {
			const entries = parseFitmentVehicleEntries(section);
			if (!entries.length) return;
			const scopes = buildStructuredFitmentScopes(entries);
			addFitmentActionColumns(section, entries, scopes);
		});
	}

	/* ==================================================================
     INIT
     ================================================================== */
	function init() {
		$overlay = document.querySelector(SEL.overlay);
		enhanceFitmentVehicleActions();
		setTimeout(enhanceFitmentVehicleActions, 300);
		if (!$overlay) return;

		$modal = $overlay.querySelector(SEL.modal);
		$garageView = $overlay.querySelector(SEL.garageView);
		$addView = $overlay.querySelector(SEL.addView);
		$vehicleList = $overlay.querySelector(SEL.vehicleList);
		$backBtn = $overlay.querySelector(SEL.backBtn);
		$formTitle = $overlay.querySelector(SEL.formTitle);
		$nativeYmmSlot = $overlay.querySelector("[data-gv2-native-ymm-slot]");
		$manualSearch = $overlay.querySelector(SEL.manualSearch);
		$vinInput = $overlay.querySelector(SEL.vinInput);
		$vinSubmit = $overlay.querySelector(SEL.vinSubmit);
		$vinError = $overlay.querySelector(SEL.vinError);
		$licenseState = $overlay.querySelector(SEL.licenseState);
		$licensePlate = $overlay.querySelector(SEL.licensePlate);
		$licenseSubmit = $overlay.querySelector(SEL.licenseSubmit);
		$licenseError = $overlay.querySelector(SEL.licenseError);
		$previewImage = $overlay.querySelector(SEL.previewImage);
		$preview = $overlay.querySelector(SEL.preview);
		$spinner = $overlay.querySelector(SEL.spinner);
		$previewTitle = $overlay.querySelector(SEL.previewTitle);
		$previewVin = $overlay.querySelector(SEL.previewVin);
		$previewPaint = $overlay.querySelector(SEL.previewPaint);
		$browseCatalog = $overlay.querySelector(SEL.browseCatalog);
		$brandLogos = $overlay.querySelector(SEL.brandLogos);
		$emptyState = $overlay.querySelector(SEL.emptyState);

		bindEvents();

		mountNativeYMM();
		document.body.classList.add("gv2-active");
		monitorManualSearchButton();
		monitorFitmentChanges();

		// Override the global toggleGaragePopup
		window.toggleGaragePopup = toggle;

		// Expose stable namespaced API
		window.GarageV2 = {
			open: open,
			openManual: openManual,
			close: close,
			toggle: toggle,
			openWithVehicleDraft: openWithVehicleDraft,
			openAndFocusSubmodel: function () {
				if (isOpen) {
					_doSubmodelFocusFlow();
				} else {
					_pendingSubmodelFocus = true;
					open();
				}
			},
		};

		// Bind header garage buttons (#easyBtn and #easyBtnMobile) to open V2 modal
		bindHeaderGarageButtons();

		// Listen for external searchTerms changes (e.g. from hero banner or other entry points)
		window.addEventListener("searchTermsUpdated", handleExternalTermsUpdate);

		/* ── Init-time vehicle bridge sync ──
		   Emit one partify:vehicle:changed event so any page-level listeners
		   (PDP gating, homepage hub, browse page) pick up the current state. */
		const initTerms = getTerms();
		if (initTerms.length) {
			syncActiveVehicle(initTerms[0]);
		} else {
			syncActiveVehicle(null);
		}
	}

	function getFocusableModalElements() {
		if (!$modal) return [];

		return Array.prototype.slice
			.call($modal.querySelectorAll(FOCUSABLE_SELECTOR))
			.filter(function (el) {
				if (el.disabled) return false;
				if (el.getAttribute("aria-hidden") === "true") return false;
				if (el.closest("[hidden]")) return false;
				return Boolean(
					el.offsetWidth || el.offsetHeight || el.getClientRects().length,
				);
			});
	}

	function trapFocus(event) {
		const focusable = getFocusableModalElements();
		if (!focusable.length) return;

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;
		const activeInsideModal = active && $modal.contains(active);

		if (!activeInsideModal) {
			event.preventDefault();
			(event.shiftKey ? last : first).focus();
			return;
		}

		if (event.shiftKey && active === first) {
			event.preventDefault();
			last.focus();
		} else if (!event.shiftKey && active === last) {
			event.preventDefault();
			first.focus();
		}
	}

	/* ==================================================================
     SUBMODEL REQUIRED HELPERS
     ================================================================== */

	/**
	 * Called every time the modal opens. If window.partifySubmodelRequired
	 * is true, adds amber highlight + inline note to the Sub Model field.
	 * Idempotent — skips if already applied.
	 */
	function applySubmodelRequired() {
		if (!window.partifySubmodelRequired || !$nativeYmmSlot) return;
		const subField = $nativeYmmSlot.querySelector(
			"[data-partify-ymm-field='submodel']",
		);
		if (!subField || subField.dataset.subReqApplied) return;
		subField.dataset.subReqApplied = "1";
		subField.classList.add("pfc-sub-required");
		const note = document.createElement("span");
		note.className = "pfc-sub-required-note";
		note.textContent = "Required for this part";
		subField.appendChild(note);
	}

	/**
	 * Opens the add/edit form pre-filled with the first garage vehicle and
	 * focuses the Sub Model field after the async preselect resolves.
	 * Used by openAndFocusSubmodel() / _pendingSubmodelFocus branch in open().
	 */
	function _doSubmodelFocusFlow() {
		const terms = getTerms();
		clearFitmentContext();
		toggleEmptyState(false);
		if (terms.length) {
			editState = { ymm: terms[0].ymm, index: 0 };
			_manualDraftRequiresConfirm = false;
			window.editCarState = { open: true, ymm: terms[0].ymm, url: "/" };
			showView("add");
			hydratePreview(terms[0]);
			const qA = $overlay.querySelector("[data-garage-quick-add]");
			const oD = $overlay.querySelector("[data-garage-or-divider]");
			if (qA) qA.style.display = "none";
			if (oD) oD.style.display = "none";
			prepareManualView(terms[0]);
		} else {
			editState = null;
			_manualDraftRequiresConfirm = false;
			showView("add");
			clearPreview();
			prepareManualView(null);
		}
		// Focus submodel after async preselect (~500 ms) + animation buffer
		setTimeout(function () {
			if (!$nativeYmmSlot) return;
			const si = $nativeYmmSlot.querySelector("#partify-ymm-submodel-input");
			// NOTE: if vehicle has no submodel options in YMM data, si will be
			// disabled and focus is skipped — this is intentional (nothing to pick).
			if (si && !si.disabled) si.focus();
		}, 900);
	}

	function setQuickAddVisible(isVisible) {
		if (!$overlay) return;
		const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
		const orDivider = $overlay.querySelector("[data-garage-or-divider]");
		if (quickAdd) quickAdd.style.display = isVisible ? "" : "none";
		if (orDivider) orDivider.style.display = isVisible ? "" : "none";
	}

	function getProductText(selector) {
		const el = document.querySelector(selector);
		return el ? String(el.textContent || "").replace(/\s+/g, " ").trim() : "";
	}

	function getProductMetaContent(selector) {
		const el = document.querySelector(selector);
		return el ? String(el.getAttribute("content") || "").trim() : "";
	}

	function getProductThumbnailUrl() {
		const img = document.querySelector(
			".product-gallery [data-gallery-selected='true'] img, .product-gallery--image img, [data-product-gallery] img, meta[property='og:image']",
		);
		if (!img) return "";
		if (img.tagName === "META") return String(img.getAttribute("content") || "");
		return String(
			img.currentSrc ||
				img.src ||
				img.getAttribute("data-src") ||
				img.getAttribute("data-zoom") ||
				"",
		).trim();
	}

	function getProductSku() {
		const skuText = getProductText(
			".product-sku:not(.product-sku--empty), [data-product-sku], [data-sku]",
		);
		return skuText.replace(/^sku\s*:\s*/i, "").trim();
	}

	function getProductTitle() {
		return (
			getProductText("#product-title") ||
			getProductText(".product-title") ||
			getProductText("[data-product-title]") ||
			getProductMetaContent("meta[property='og:title']")
		);
	}

	function clearFitmentContext() {
		if (!$overlay) return;
		const context = $overlay.querySelector("[data-gv2-fitment-context]");
		if (context) context.remove();
		const rowNotice = $overlay.querySelector("[data-gv2-fitment-row-notice]");
		if (rowNotice) rowNotice.remove();
		const preview = $overlay.querySelector("[data-gv2-fitment-preview]");
		if (preview) preview.remove();
		const disclaimer = $overlay.querySelector(".disclaimer");
		if (disclaimer) disclaimer.style.display = "";
		_fitmentDraftPreviewSeq += 1;
		_fitmentDraftPreviewState = "idle";
	}

	function renderFitmentContext(draft) {
		clearFitmentContext();
		if (!$addView || !$nativeYmmSlot || !draft) return;

		const title = getProductTitle();
		const sku = getProductSku();
		const imageUrl = getProductThumbnailUrl();
		const scope = String(
			draft.fitmentLabel ||
				[
					draft.allowedYears && draft.allowedYears.length
						? draft.allowedYears.slice().reverse().join("-")
						: draft.year,
					draft.make,
					draft.model,
				]
					.filter(Boolean)
					.join(" "),
		)
			.replace(/\s+/g, " ")
			.trim();

		if (!title && !sku && !imageUrl && !scope) return;

		const context = document.createElement("div");
		context.className = "gv2-fitment-context";
		context.dataset.gv2FitmentContext = "true";

		if (imageUrl) {
			const imageWrap = document.createElement("div");
			imageWrap.className = "gv2-fitment-context__image";
			const image = document.createElement("img");
			image.src = imageUrl;
			image.alt = "";
			image.loading = "lazy";
			image.width = 64;
			image.height = 64;
			imageWrap.appendChild(image);
			context.appendChild(imageWrap);
		}

		const body = document.createElement("div");
		body.className = "gv2-fitment-context__body";

		const label = document.createElement("div");
		label.className = "gv2-fitment-context__label";
		label.textContent = "Select a vehicle for this part";
		body.appendChild(label);

		if (title) {
			const titleEl = document.createElement("div");
			titleEl.className = "gv2-fitment-context__title";
			titleEl.textContent = title;
			body.appendChild(titleEl);
		}

		if (sku) {
			const skuEl = document.createElement("div");
			skuEl.className = "gv2-fitment-context__meta";
			skuEl.textContent = "Part " + sku;
			body.appendChild(skuEl);
		}

		if (scope) {
			const scopeEl = document.createElement("div");
			scopeEl.className = "gv2-fitment-context__scope";
			scopeEl.textContent = "Available fitment: " + scope;
			body.appendChild(scopeEl);
		}

		context.appendChild(body);
		const manualSection = $nativeYmmSlot.closest(".gv2-manual-section");
		const firstAddContent =
			$addView.querySelector("[data-garage-quick-add]") ||
			$addView.querySelector(".disclaimer") ||
			manualSection;
		if (firstAddContent && firstAddContent.parentNode) {
			firstAddContent.parentNode.insertBefore(context, firstAddContent);
		}
	}

	function renderFitmentRowNotice() {
		if (!$addView || !$nativeYmmSlot) return;
		const disclaimer = $overlay.querySelector(".disclaimer");
		if (disclaimer) disclaimer.style.display = "none";
		const existing = $overlay.querySelector("[data-gv2-fitment-row-notice]");
		if (existing) existing.remove();

		const notice = document.createElement("p");
		notice.className = "gv2-fitment-row-notice";
		notice.dataset.gv2FitmentRowNotice = "true";
		notice.textContent =
			"Choose a vehicle for this part. Fitment will be checked after you add the vehicle.";

		const manualSection = $nativeYmmSlot.closest(".gv2-manual-section");
		if (manualSection && manualSection.parentNode) {
			manualSection.parentNode.insertBefore(notice, manualSection);
		}
	}

	function getFitmentPreviewNode() {
		if (!$overlay || !$nativeYmmSlot || !$manualSearch) return null;
		let preview = $overlay.querySelector("[data-gv2-fitment-preview]");
		if (preview) return preview;

		preview = document.createElement("div");
		preview.className = "gv2-fitment-preview";
		preview.dataset.gv2FitmentPreview = "true";
		preview.setAttribute("role", "status");
		preview.setAttribute("aria-live", "polite");

		if ($manualSearch.parentNode) {
			$manualSearch.parentNode.insertBefore(preview, $manualSearch);
		}
		return preview;
	}

	function setFitmentPreviewMessage(state, message) {
		if (!_manualDraftRequiresConfirm) return;
		const preview = getFitmentPreviewNode();
		if (!preview) return;
		_fitmentDraftPreviewState = state;
		preview.className = "gv2-fitment-preview gv2-fitment-preview--" + state;
		preview.textContent = message;
		updateManualSearchDisabledState();
	}

	function buildCurrentDraftVehicle() {
		const sel = getManualSelects();
		if (!sel) return null;
		const year = String(sel.year?.value || "").trim();
		const make = String(sel.make?.value || "").trim();
		const model = String(sel.model?.value || "").trim();
		if (!year || !make || !model) return null;
		const submodel = String(sel.submodel?.value || "").trim();
		const engine = String(sel.engine?.value || "").trim();
		return {
			year: year,
			make: make,
			model: model,
			submodel: submodel,
			engine: engine,
			ymm: [year, make, model, submodel, engine].filter(Boolean).join(" "),
		};
	}

	function queueDraftFitmentPreview() {
		if (!_manualDraftRequiresConfirm) return;
		const token = ++_fitmentDraftPreviewSeq;
		const draft = buildCurrentDraftVehicle();

		if (!draft) {
			setFitmentPreviewMessage(
				"incomplete",
				"Choose vehicle details to check fitment.",
			);
			return;
		}

		Promise.resolve().then(function () {
			if (token !== _fitmentDraftPreviewSeq || !_manualDraftRequiresConfirm) {
				return;
			}
			evaluateDraftFitment(draft, token);
		});
	}

	function evaluateDraftFitment(draft, token) {
		const resolver = window.PartifyFitment;
		if (!resolver || typeof resolver.resolveVehicle !== "function") {
			setFitmentPreviewMessage(
				"neutral",
				"Fitment will be checked after you add this vehicle and complete any required product options.",
			);
			return;
		}

		const result = resolver.resolveVehicle(draft);
		if (token !== _fitmentDraftPreviewSeq || !_manualDraftRequiresConfirm) {
			return;
		}

		if (result && result.state === "fail") {
			setFitmentPreviewMessage(
				"fail",
				"This part does not fit the selected configuration. Choose another submodel, engine, or vehicle.",
			);
			return;
		}

		if (result && result.state === "success") {
			setFitmentPreviewMessage(
				"success",
				"Vehicle matches this product's vehicle fitment. Additional product options may still affect final fitment.",
			);
			return;
		}

		setFitmentPreviewMessage(
			"neutral",
			"Fitment will be checked after you add this vehicle and complete any required product options.",
		);
	}

	function vehicleMatchesFitmentRequest(candidate, expected) {
		if (!candidate || !expected) return false;
		const candidateYear = String(candidate.year || "").trim();
		const expectedYear = String(expected.year || "").trim();
		return (
			candidateYear === expectedYear &&
			normalizeFitmentText(candidate.make) ===
				normalizeFitmentText(expected.make) &&
			normalizeFitmentText(candidate.model) ===
				normalizeFitmentText(expected.model)
		);
	}

	function fitmentCardTextMatchesVehicle(card, vehicle) {
		const vehicleText = card
			? normalizeFitmentText(
					card.querySelector("[data-pfc-vehicle]")?.textContent || "",
				)
			: "";
		if (!vehicleText) return false;
		return [vehicle.year, vehicle.make, vehicle.model].every(function (part) {
			const normalizedPart = normalizeFitmentText(part);
			return normalizedPart && vehicleText.indexOf(normalizedPart) > -1;
		});
	}

	function getStickyHeaderOffset() {
		const selectors = [
			".site-header-wrapper",
			".site-header",
			".header-wrapper",
			".shopify-section-group-header-group",
		];
		let offset = 0;
		selectors.forEach(function (selector) {
			const el = document.querySelector(selector);
			if (!el) return;
			const style = window.getComputedStyle(el);
			if (style.position !== "fixed" && style.position !== "sticky") return;
			const rect = el.getBoundingClientRect();
			if (rect.height <= 0 || rect.top > 4) return;
			offset = Math.max(offset, rect.bottom);
		});
		return Math.max(0, offset) + 14;
	}

	function focusFitmentResult(card) {
		const target =
			card.querySelector(".pfc__headline") ||
			card.querySelector("[data-pfc-label]") ||
			card;
		if (!target) return;
		if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
		try {
			target.focus({ preventScroll: true });
		} catch (e) {
			target.focus();
		}
	}

	function scrollFitmentResultIntoView(card) {
		if (!card) return;
		const prefersReducedMotion = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		const top = Math.max(
			0,
			card.getBoundingClientRect().top +
				window.pageYOffset -
				getStickyHeaderOffset(),
		);
		window.scrollTo({
			top: top,
			behavior: prefersReducedMotion ? "auto" : "smooth",
		});
		setTimeout(
			function () {
				focusFitmentResult(card);
			},
			prefersReducedMotion ? 0 : 260,
		);
	}

	function scheduleFitmentResultScroll(vehicle) {
		if (!window.location.pathname.includes("/products/")) return;
		if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model) return;

		const expectedVehicle = {
			year: String(vehicle.year || "").trim(),
			make: String(vehicle.make || "").trim(),
			model: String(vehicle.model || "").trim(),
		};
		const token = ++_fitmentResultScrollToken;
		let observer = null;
		const timers = [];

		const cleanup = function () {
			document.removeEventListener(
				"partify:native-fitment-resolved",
				onNativeFitmentResolved,
			);
			if (observer) observer.disconnect();
			timers.forEach(function (timer) {
				clearTimeout(timer);
			});
		};

		const findUpdatedCard = function () {
			const card = document.querySelector("[data-partify-fitment-card]");
			if (!card) return null;
			const state = card.getAttribute("data-pfc-state");
			if (state !== "success" && state !== "fail") return null;
			const terms = getTerms();
			if (
				terms.length &&
				!vehicleMatchesFitmentRequest(terms[0], expectedVehicle)
			) {
				return null;
			}
			if (!fitmentCardTextMatchesVehicle(card, expectedVehicle)) return null;
			return card;
		};

		const tryScroll = function () {
			if (token !== _fitmentResultScrollToken) {
				cleanup();
				return;
			}
			const card = findUpdatedCard();
			if (!card) return;
			cleanup();
			scrollFitmentResultIntoView(card);
		};

		const queueCheck = function (delay) {
			timers.push(setTimeout(tryScroll, delay));
		};

		function onNativeFitmentResolved(event) {
			const detail = event.detail || {};
			if (detail.state !== "success" && detail.state !== "fail") return;
			if (
				detail.vehicle &&
				!vehicleMatchesFitmentRequest(detail.vehicle, expectedVehicle)
			) {
				return;
			}
			queueCheck(120);
			queueCheck(320);
		}

		document.addEventListener(
			"partify:native-fitment-resolved",
			onNativeFitmentResolved,
		);

		const card = document.querySelector("[data-partify-fitment-card]");
		if (card) {
			observer = new MutationObserver(function () {
				queueCheck(60);
			});
			observer.observe(card, {
				attributes: true,
				attributeFilter: ["data-pfc-state"],
				childList: true,
				subtree: true,
			});
		}

		queueCheck(160);
		queueCheck(700);
		timers.push(setTimeout(cleanup, 6500));
	}

	function normalizeVehicleDraft(draft) {
		if (!draft || typeof draft !== "object") return null;
		const make = String(draft.make || "").trim();
		const model = String(draft.model || "").trim();
		const requireExplicitSelections = draft.requireExplicitSelections === true;
		const allowedYears = Array.isArray(draft.allowedYears)
			? draft.allowedYears
					.map(function (year) {
						return String(year || "").trim();
					})
					.filter(function (year, index, years) {
						return /^\d{4}$/.test(year) && years.indexOf(year) === index;
					})
					.sort(function (a, b) {
						return Number(b) - Number(a);
					})
			: [];
		let year = String(draft.year || "").trim();
		if (year && !/^\d{4}$/.test(year)) year = "";
		if (!year && allowedYears.length === 1 && !requireExplicitSelections) {
			year = allowedYears[0];
		}
		if (!make || !model || (!year && !allowedYears.length)) return null;
		if (year && !allowedYears.length) allowedYears.push(year);
		const fitmentLabel = String(draft.fitmentLabel || "")
			.replace(/\s+/g, " ")
			.trim();
		const submodel = String(draft.submodel || "").trim();
		const engine = String(draft.engine || "").trim();
		const allowedVehicles = Array.isArray(draft.allowedVehicles)
			? draft.allowedVehicles
					.map(function (vehicle) {
						return {
							year: cleanFitmentYear(vehicle && vehicle.year),
							make: String((vehicle && vehicle.make) || "").trim(),
							model: String((vehicle && vehicle.model) || "").trim(),
							submodel: String((vehicle && vehicle.submodel) || "").trim(),
							engine: String((vehicle && vehicle.engine) || "").trim(),
						};
					})
					.filter(function (vehicle, index, vehicles) {
						const key = [
							vehicle.year,
							normalizeFitmentText(vehicle.make),
							normalizeFitmentText(vehicle.model),
							normalizeFitmentText(vehicle.submodel),
							normalizeFitmentText(vehicle.engine),
						].join("|");
						return (
							vehicle.year &&
							vehicle.make &&
							vehicle.model &&
							allowedYears.includes(vehicle.year) &&
							normalizeFitmentText(vehicle.make) === normalizeFitmentText(make) &&
							normalizeFitmentText(vehicle.model) === normalizeFitmentText(model) &&
							vehicles.findIndex(function (candidate) {
								return (
									[
										candidate.year,
										normalizeFitmentText(candidate.make),
										normalizeFitmentText(candidate.model),
										normalizeFitmentText(candidate.submodel),
										normalizeFitmentText(candidate.engine),
									].join("|") === key
								);
							}) === index
						);
					})
			: [];
		return {
			year: year,
			make: make,
			model: model,
			submodel: submodel,
			engine: engine,
			allowedYears: allowedYears,
			allowedVehicles: allowedVehicles,
			fitmentLabel: fitmentLabel,
			requireExplicitSelections: requireExplicitSelections,
			ymm: [year, make, model, submodel, engine].filter(Boolean).join(" "),
		};
	}

	function openWithVehicleDraft(draft) {
		const vehicleDraft = normalizeVehicleDraft(draft);
		if (!vehicleDraft) {
			open();
			return;
		}

		_manualDraftRequiresConfirm = true;
		if (!isOpen) open();
		_manualDraftRequiresConfirm = true;
		mountNativeYMM();
		editState = null;
		if (window.editCarState) window.editCarState = { open: false };
		showView("add");
		toggleEmptyState(false);
		clearPreview();
		setQuickAddVisible(false);
		renderFitmentContext(vehicleDraft);
		renderFitmentRowNotice();
		prepareManualView(vehicleDraft);
		setFitmentPreviewMessage(
			"incomplete",
			"Choose vehicle details to check fitment.",
		);
		queueDraftFitmentPreview();

		setTimeout(function () {
			if (!_nativeYmmInstance) return;
			if (!vehicleDraft.year) {
				_nativeYmmInstance.openFirst();
				return;
			}
			const modelInput = $nativeYmmSlot.querySelector("#partify-ymm-model-input");
			if (modelInput && !modelInput.disabled) modelInput.focus();
		}, 600);
	}

	/* ==================================================================
     OPEN / CLOSE
     ================================================================== */
	function toggle() {
		if (isOpen) close();
		else open();
	}

	function open() {
		if (isOpen) return;
		isOpen = true;
		// Snapshot first vehicle so close() can detect changes on PDP
		const t = getTerms();
		_vehicleAtOpenTime = t.length ? t[0].ymm : null;
		mountNativeYMM();
		applySubmodelRequired();
		monitorManualSearchButton(); // restart on each open (cleaned up on close)
		const scrollY = window.scrollY;
		document.body.dataset.gv2ScrollY = scrollY;

		// Scroll lock
		let lockStyle = document.getElementById("gv2-lock-style");
		if (!lockStyle) {
			lockStyle = document.createElement("style");
			lockStyle.id = "gv2-lock-style";
			document.head.appendChild(lockStyle);
		}
		lockStyle.textContent = `body.gv2-locked{position:fixed;top:-${scrollY}px;left:0;width:100%;overflow:hidden!important;}`;
		document.body.classList.add("gv2-locked");

		$overlay.classList.remove("hidden");

		// Decide which view to show — single getTerms() call
		const terms = getTerms();
		if (_pendingSubmodelFocus) {
			_pendingSubmodelFocus = false;
			_doSubmodelFocusFlow();
		} else if (terms.length) {
			showView("garage", terms);
			renderVehicleList(terms);
			hydratePreview(terms[0]);
			toggleEmptyState(false);
		} else {
			// Skip empty-state truck animation — go straight to add form
			editState = null;
			_manualDraftRequiresConfirm = false;
			resetVinForm();
			showView("add");
			toggleEmptyState(false);
			clearPreview();
			prepareManualView(null);

			// Auto-open Year field after modal animation settles
			setTimeout(function () {
				if (_nativeYmmInstance) _nativeYmmInstance.openFirst();
			}, 350);
		}

		// Hide old Shopify chat
		if (typeof hideShopifyChat === "function") hideShopifyChat();
		window.igEvents = window.igEvents || [];
		window.igEvents.push({ event: "garage_opened" });
	}

	function openManual() {
		open();
		const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
		if (!addBtn) return false;
		addBtn.click();
		return true;
	}

	function close() {
		if (!isOpen) return;
		isOpen = false;
		$overlay.classList.add("hidden");

		const savedY = parseInt(document.body.dataset.gv2ScrollY || "0", 10);
		document.body.classList.remove("gv2-locked");
		const lockStyle = document.getElementById("gv2-lock-style");
		if (lockStyle) lockStyle.remove();
		window.scrollTo(0, savedY);

		editState = null;
		_manualDraftRequiresConfirm = false;
		setQuickAddVisible(true);
		clearFitmentContext();
		if (window.editCarState) window.editCarState.open = false;

		cleanupTimers();
		if (typeof showShopifyChat === "function") showShopifyChat();

		// On product pages, reload if the active vehicle changed so the
		// fitment widget, Add-to-Cart gating, and paint options all
		// re-evaluate against the correct vehicle.
		if (window.location.pathname.includes("/products/")) {
			const cur = getTerms();
			const curYmm = cur.length ? cur[0].ymm : null;
			if (curYmm !== _vehicleAtOpenTime) {
				// Strip ?fitment param if present so the page doesn't re-gate
				const _hasFitment = new URLSearchParams(window.location.search).has(
					"fitment",
				);
				if (_hasFitment) {
					const _url = new URL(window.location.href);
					_url.searchParams.delete("fitment");
					history.replaceState(null, "", _url.toString());
				}
				// Do NOT reload — partify:vehicle:changed (fired by syncActiveVehicle)
				// re-runs the fitment resolver, paint options, and Add-to-Cart gating.
			}
		}
	}

	function cleanupTimers() {
		if (_manualBtnTimer) {
			clearInterval(_manualBtnTimer);
			_manualBtnTimer = null;
		}
		if (_autoSubmitTimer) {
			clearTimeout(_autoSubmitTimer);
			_autoSubmitTimer = null;
		}
		// Note: _fitmentPollTimer keeps running — it monitors background changes
	}

	/* ==================================================================
     VIEW SWITCHING
     ================================================================== */
	function showView(name, termsOpt) {
		if (name === "garage") {
			clearFitmentContext();
			$garageView.style.display = "";
			$addView.style.display = "none";
			$backBtn.style.display = "none";
		} else {
			$garageView.style.display = "none";
			$addView.style.display = "";
			const terms = termsOpt || getTerms();
			$backBtn.style.display = terms.length > 0 ? "" : "none";
			if ($formTitle) {
				$formTitle.textContent = editState
					? i18n("editVehicle")
					: i18n("selectVehicle");
			}
		}
	}

	/* ==================================================================
     EMPTY STATE — truck loader toggle
     ================================================================== */
	function toggleEmptyState(show) {
		if (!$emptyState) return;
		if (show) {
			$emptyState.style.display = "";
			if ($vehicleList) $vehicleList.style.display = "none";
			// Hide the "Add Vehicle" dashed button since the CTA is inside empty state
			const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
			if (addBtn) addBtn.style.display = "none";
		} else {
			$emptyState.style.display = "none";
			if ($vehicleList) $vehicleList.style.display = "";
			const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
			if (addBtn) addBtn.style.display = "";
		}
	}

	/* ==================================================================
     TABS
     ================================================================== */
	function activateTab(tabName) {
		const allTabs = $overlay.querySelectorAll(SEL.tab);
		const allPanels = $overlay.querySelectorAll(SEL.panel);
		allTabs.forEach((t) =>
			t.classList.toggle("active", t.dataset.gv2Tab === tabName),
		);
		allPanels.forEach((p) =>
			p.classList.toggle("active", p.dataset.gv2Panel === tabName),
		);
	}

	/* ==================================================================
     VEHICLE LIST RENDERING
     ================================================================== */
	function isMatchByVinPaintCode(value) {
		const normalized = String(value || "").trim();
		return (
			normalized.toUpperCase() === ".VIN" ||
			/match\s+(paint\s+)?by\s+vin/i.test(normalized)
		);
	}

	function vehiclePaintDisplayLabel(vehicle) {
		if (!vehicle) return "";
		if (isMatchByVinPaintCode(vehicle.paintCode)) return "Match Paint by VIN";
		const paintCode = String(vehicle.paintCode || "").trim();
		const fallbackPaintCode = isMatchByVinPaintCode(vehicle.troublesome_format)
			? ""
			: String(vehicle.troublesome_format || "").trim();
		const exactPaintCode = paintCode || fallbackPaintCode;
		const description = String(vehicle.troublesome_description || "").trim();
		if (exactPaintCode && description) {
			return `${exactPaintCode} (${description})`;
		}
		return exactPaintCode || description;
	}

	function renderVehicleList(terms) {
		if (!$vehicleList) return;

		const newHtml = terms
			.slice(0, 5)
			.map((v, i) => {
				const isActive = i === 0;
				const cls = isActive ? " active" : "";
				const vinHtml = v.vin
					? `<span class="gv2-vehicle-item__meta">VIN: ${escHtml(v.vin)}</span>`
					: "";
				// Build paint display: code + description, code only, description only, or hide
				const paintLabel = vehiclePaintDisplayLabel(v);
				const paintHtml = paintLabel
					? `<span class="gv2-vehicle-item__meta">Paint: ${escHtml(paintLabel)}</span>`
					: "";
				const metaHtml =
					vinHtml || paintHtml
						? `<div class="gv2-vehicle-item__metas">${vinHtml}${paintHtml}</div>`
						: "";
				const ctaHtml = isActive
					? `<button class="gv2-vehicle-item__cta" type="button" data-gv2-action="shop" data-gv2-idx="${i}" data-garage-active-cta>Shop Parts for this Vehicle</button>`
					: "";

				return `
        <li class="gv2-vehicle-item${cls}" data-gv2-vi="${i}" data-gv2-ymm="${escAttr(v.ymm)}" data-garage-vehicle>
          <div class="gv2-vehicle-item__info">
            <div class="gv2-vehicle-item__name">${escHtml(v.ymm)}</div>
            ${metaHtml}
          </div>
          <button class="gv2-vehicle-item__edit" type="button" data-gv2-action="edit" data-gv2-idx="${i}" aria-label="Edit vehicle">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
          </button>
          <button class="gv2-vehicle-item__delete" type="button" data-gv2-action="delete" data-gv2-idx="${i}" aria-label="Remove vehicle">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M7 4V2h10v2h5v2h-2v15a1 1 0 01-1 1H5a1 1 0 01-1-1V6H2V4h5zM6 6v14h12V6H6zm3 3h2v8H9V9zm4 0h2v8h-2V9z"/></svg>
          </button>
          ${ctaHtml}
        </li>`;
			})
			.join("");

		/* If the HTML is identical, skip the DOM swap entirely */
		if ($vehicleList.innerHTML.trim() === newHtml.trim()) return;

		/* Direct swap — no animation needed. Redundant re-renders are
		   already blocked by _internalActionInProgress, so this only
		   fires once per user action. */
		$vehicleList.innerHTML = newHtml;
	}

	/* ==================================================================
     VEHICLE PREVIEW CARD
     ================================================================== */
	function hydratePreview(vehicle) {
		if (!vehicle) {
			clearPreview();
			return;
		}
		currentActiveVehicle = vehicle;

		const ymm = vehicle.ymm || "";
		const shortTitle =
			[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(" ") ||
			ymm;

		// Swap: hide brand logos, show preview panel
		if ($brandLogos) $brandLogos.classList.add("hidden");
		if ($preview) $preview.style.display = "";

		/* Only apply the fade-out transition when the image will need a
		   network fetch. For cached images the preload in setPreviewImage
		   completes near-instantly so fading out just causes a needless
		   flicker. */
		const _previewHandle = [vehicle.year, vehicle.make, vehicle.model]
			.filter(Boolean)
			.join(" ")
			.trim()
			.toLowerCase()
			.replace(/[./]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w\-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");
		if (
			!imageCache[_previewHandle] &&
			$preview &&
			$preview.classList.contains("gv2-preview--ready")
		) {
			$preview.classList.add("gv2-preview--switching");
		}

		// Show info
		$previewTitle.style.display = "";
		$previewTitle.textContent = shortTitle;

		if (vehicle.vin) {
			$previewVin.style.display = "";
			$previewVin.textContent = "VIN: " + vehicle.vin;
		} else {
			$previewVin.style.display = "none";
		}

		const paintLabel = vehiclePaintDisplayLabel(vehicle);
		if (paintLabel) {
			$previewPaint.style.display = "";
			$previewPaint.textContent = isMatchByVinPaintCode(vehicle.paintCode)
				? "Paint: " + paintLabel
				: "Paint Code: " + paintLabel;
		} else {
			$previewPaint.style.display = "none";
		}

		// Browse button is now inside the vehicle card (data-garage-active-cta)
		// Right-panel browse kept hidden; CTA lives in the active vehicle item.

		// Fetch image
		fetchVehicleImage(vehicle);
	}

	function clearPreview() {
		currentActiveVehicle = null;
		$previewTitle.style.display = "none";
		$previewVin.style.display = "none";
		$previewPaint.style.display = "none";
		$previewImage.classList.remove("loaded", "blueprint");
		$previewImage.src = "";
		$spinner.classList.remove("active");
		$browseCatalog.classList.remove("visible");

		// Swap: hide preview panel, show brand logos
		if ($preview) $preview.style.display = "none";
		if ($brandLogos) $brandLogos.classList.remove("hidden");
	}

	async function fetchVehicleImage(vehicle) {
		const year = (vehicle.year || "").toString().trim();
		const make = (vehicle.make || "").toString().trim();
		const model = (vehicle.model || "").toString().trim();
		const titleShort = [year, make, model].filter(Boolean).join(" ").trim();
		const fallback = cfg().fallbackImageUrl || "";

		// DEBUG: Log fallback URL
		console.log("[Garage Debug] Fallback image URL:", fallback);

		if (!titleShort) {
			// No year/make/model - show fallback immediately if available
			if (fallback) {
				setPreviewImage(fallback, "");
			} else {
				$spinner.classList.remove("active");
			}
			return;
		}

		const handle = titleShort
			.toLowerCase()
			.replace(/[./]/g, "-")
			.replace(/\s+/g, "-")
			.replace(/[^\w\-]+/g, "")
			.replace(/--+/g, "-")
			.replace(/-+$/, "");

		// Check cache
		if (imageCache[handle]) {
			setPreviewImage(imageCache[handle], titleShort);
			return;
		}

		// Show loading
		$spinner.classList.add("active");

		try {
			const res = await fetch(
				"/collections/" + handle + "?view=vehicle-image",
				{ credentials: "same-origin" },
			);
			if (!res.ok) throw new Error(res.status);
			const data = JSON.parse(await res.text());
			console.log("[Garage Debug] Collection image fetch result:", data);
			if (data.image && !looksLikePartImage(data.image)) {
				imageCache[handle] = data.image;
				setPreviewImage(data.image, data.title || titleShort);
			} else if (fallback) {
				// Use blueprint fallback
				console.log("[Garage Debug] Using fallback image");
				imageCache[handle] = fallback;
				setPreviewImage(fallback, titleShort);
			} else {
				console.log("[Garage Debug] No image or fallback available");
				$spinner.classList.remove("active");
			}
		} catch (e) {
			// Network error or missing collection — try fallback
			if (fallback) {
				imageCache[handle] = fallback;
				setPreviewImage(fallback, titleShort);
			} else {
				$spinner.classList.remove("active");
			}
		}
	}

	function setPreviewImage(src, alt) {
		const fallback = cfg().fallbackImageUrl || "";
		const isBlueprintImg = fallback && src === fallback;

		/* If the image is already showing this exact src, skip — no work. */
		const currentSrc = $previewImage.getAttribute("src") || "";
		if (currentSrc === src) {
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
			return;
		}

		/* Preload the new image in a detached <img> so the old image stays
		   fully visible until the new one is ready — zero blank frames.
		   But if there's no current image showing, skip the preload and
		   set src directly so we don't stare at a blank preview. */
		const hasCurrent = $previewImage.getAttribute("src");
		if (!hasCurrent) {
			$previewImage.onload = function () {
				$previewImage.classList.add("loaded");
				$previewImage.classList.toggle("blueprint", isBlueprintImg);
				$spinner.classList.remove("active");
				if ($preview) {
					$preview.classList.remove("gv2-preview--switching");
					$preview.classList.add("gv2-preview--ready");
				}
			};
			$previewImage.onerror = function () {
				/* Even on error, mark as loaded so the image (or broken
				   icon) is visible instead of staying at opacity 0 */
				$previewImage.classList.add("loaded");
				$previewImage.classList.toggle("blueprint", isBlueprintImg);
				$spinner.classList.remove("active");
				if ($preview) {
					$preview.classList.remove("gv2-preview--switching");
					$preview.classList.add("gv2-preview--ready");
				}
			};
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			return;
		}

		const tempImg = new Image();
		tempImg.onload = function () {
			/* Swap src only after new image is decoded / cached */
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
		};
		tempImg.onerror = function () {
			/* Still set the src so the fallback/blueprint shows even if
			   the preload reported an error (e.g. CORS, redirect). */
			$previewImage.src = src;
			$previewImage.alt = alt || "";
			$previewImage.classList.add("loaded");
			$previewImage.classList.toggle("blueprint", isBlueprintImg);
			$spinner.classList.remove("active");
			if ($preview) {
				$preview.classList.remove("gv2-preview--switching");
				$preview.classList.add("gv2-preview--ready");
			}
		};
		/* Don't remove 'loaded' — keep old image visible while new one loads */
		tempImg.src = src;
	}

	function looksLikePartImage(src) {
		const s = String(src || "").toLowerCase();
		const kw = [
			"bumper",
			"fender",
			"grille",
			"fog",
			"bracket",
			"support",
			"insert",
			"bezel",
			"deflector",
			"mirror",
			"door",
			"panel",
			"hood",
			"cover",
			"valance",
			"radiator",
			"headlight",
			"taillight",
			"painted",
			"painted-to-match",
			"ships-unfolded",
			"shipped-unfolded",
			"hardware",
			"kit",
		];
		for (let i = 0; i < kw.length; i++) {
			if (s.indexOf(kw[i]) > -1) return true;
		}
		return false;
	}

	/* ==================================================================
     NATIVE YMM
     ================================================================== */

	/**
	 * Mount the native YMM selector into $nativeYmmSlot.
	 * Safe to call multiple times — init is skipped if already mounted.
	 */
	function mountNativeYMM() {
		if (!window.PartifyYMM || !$nativeYmmSlot) return;
		if (_nativeYmmInstance) return; // already mounted

		$nativeYmmSlot.hidden = false;

		_nativeYmmInstance = window.PartifyYMM.init($nativeYmmSlot, {
			vehicleCandidateFilter: function (vehicle) {
				if (!_manualDraftRequiresConfirm) return true;
				return rowFitmentCandidatePasses(vehicle);
			},
			onReadyChange: function (ready) {
				updateManualSearchDisabledState();
				queueDraftFitmentPreview();
				// Auto-submit: when all required fields are filled, trigger Add Vehicle
				// Skip auto-submit when submodel is required but not yet chosen
				clearTimeout(_autoSubmitTimer);
				const subOk =
					!window.partifySubmodelRequired ||
					!!getManualSelects()?.submodel?.value;
				if (ready && subOk && !editState && !_manualDraftRequiresConfirm) {
					_autoSubmitTimer = setTimeout(function () {
						const subStillOk =
							!window.partifySubmodelRequired ||
							!!getManualSelects()?.submodel?.value;
						const willClick = !!(
							_nativeYmmInstance &&
							_nativeYmmInstance.isReady() &&
							subStillOk &&
							!editState &&
							!_manualDraftRequiresConfirm &&
							$manualSearch &&
							!$manualSearch.disabled
						);
						if (willClick) {
							$manualSearch.click();
						}
					}, 300);
				}
			},
		});
	}

	/**
	 * Prepare the manual-entry section for adding or editing a vehicle.
	 * @param {object|null} vehicleToPreselect — vehicle to pre-fill, or null for blank add form.
	 */
	function prepareManualView(vehicleToPreselect) {
		if ($nativeYmmSlot) $nativeYmmSlot.hidden = false;
		if (vehicleToPreselect) {
			if (_nativeYmmInstance) _nativeYmmInstance.preselect(vehicleToPreselect);
		} else {
			if (_nativeYmmInstance) _nativeYmmInstance.reset();
		}
	}

	/* ==================================================================
     MANUAL SEARCH BUTTON SYNC
     ================================================================== */
	function getManualSelects() {
		if (!$nativeYmmSlot) return null;
		return {
			year: $nativeYmmSlot.querySelector("#partify-ymm-year"),
			make: $nativeYmmSlot.querySelector("#partify-ymm-make"),
			model: $nativeYmmSlot.querySelector("#partify-ymm-model"),
			submodel: $nativeYmmSlot.querySelector("#partify-ymm-submodel"),
			engine: $nativeYmmSlot.querySelector("#partify-ymm-engine"),
		};
	}

	function updateManualSearchDisabledState() {
		if (!$manualSearch) return;
		const sel = getManualSelects();
		if (!sel) return;
		const ymmOk = !!(sel.year?.value && sel.make?.value && sel.model?.value);
		const subOk = !window.partifySubmodelRequired || !!sel.submodel?.value;
		const fitmentBlocked =
			_manualDraftRequiresConfirm && _fitmentDraftPreviewState === "fail";
		$manualSearch.disabled = !(ymmOk && subOk) || fitmentBlocked;
	}

	function monitorManualSearchButton() {
		// Enable Add Vehicle when required fields are filled.
		if (_manualBtnTimer) clearInterval(_manualBtnTimer);
		_manualBtnTimer = setInterval(() => {
			updateManualSearchDisabledState();
		}, 600);
	}

	/* ==================================================================
     MONITOR FITMENT CHANGES (same as old garage-script polling)
     ================================================================== */
	function monitorFitmentChanges() {
		const FITMENT_KEY = "easysearch-preselect-fitment";
		const getFitValue = () => {
			try {
				return JSON.parse(localStorage.getItem(FITMENT_KEY))?.value ?? null;
			} catch (e) {
				return null;
			}
		};
		let lastVal = getFitValue();

		if (_fitmentPollTimer) clearInterval(_fitmentPollTimer);
		_fitmentPollTimer = setInterval(() => {
			// Skip view-switch while editing.
			if (editState) return;

			const cur = getFitValue();
			if (cur === lastVal) return;
			lastVal = cur;

			try {
				if (!cur) return;

				const ymm = cur
					.replace(/easysearch-preselect-delimiter$/, "")
					.split("easysearch-preselect-delimiter")
					.join(" ")
					.trim();

				if (!ymm) return;

				// Refresh the list & switch to garage view
				const terms = getTerms();
				if (isOpen) {
					showView("garage", terms);
					toggleEmptyState(!terms.length);
					renderVehicleList(terms);
					if (terms.length) hydratePreview(terms[0]);
				}
				if (terms.length) syncActiveVehicle(terms[0]);
			} catch (e) {
				/* ignore */
			}
		}, 800);
	}

	/* ==================================================================
     EVENT BINDING
     ================================================================== */
	function bindEvents() {
		// Close
		$overlay.querySelector(SEL.close).addEventListener("click", close);
		$overlay.addEventListener("click", (e) => {
			if (e.target === $overlay) close();
		});
		if ($modal) {
			$modal.addEventListener("click", (e) => {
				e.stopPropagation();
			});
		}
		document.addEventListener("keydown", (e) => {
			if (e.key === "Escape" && isOpen) close();
			if (e.key === "Tab" && isOpen) trapFocus(e);
		});

		// Add Vehicle
		const addBtn = $overlay.querySelector(SEL.addVehicleBtn);
		if (addBtn) {
			addBtn.addEventListener("click", () => {
				editState = null;
				_manualDraftRequiresConfirm = false;
				clearFitmentContext();
				resetVinForm();
				showView("add");
				toggleEmptyState(false);
				clearPreview();
				// Show quick-add section when adding new vehicle
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				prepareManualView(null);
			});
		}

		// Add Vehicle from empty state CTA
		const addEmptyBtn = $overlay.querySelector(SEL.addVehicleEmpty);
		if (addEmptyBtn) {
			addEmptyBtn.addEventListener("click", () => {
				editState = null;
				_manualDraftRequiresConfirm = false;
				clearFitmentContext();
				resetVinForm();
				showView("add");
				toggleEmptyState(false);
				clearPreview();
				// Show quick-add section when adding new vehicle
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				prepareManualView(null);
			});
		}

		// Back to garage
		if ($backBtn) {
			$backBtn.addEventListener("click", () => {
				editState = null;
				_manualDraftRequiresConfirm = false;
				clearFitmentContext();
				// Restore quick-add section visibility when returning to garage
				const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
				const orDivider = $overlay.querySelector("[data-garage-or-divider]");
				if (quickAdd) quickAdd.style.display = "";
				if (orDivider) orDivider.style.display = "";
				const terms = getTerms();
				showView("garage", terms);
				renderVehicleList(terms);
				toggleEmptyState(!terms.length);
				if (terms.length) hydratePreview(terms[0]);
			});
		}

		// Vehicle list actions (delegation)
		if ($vehicleList) {
			$vehicleList.addEventListener("click", handleVehicleListClick);
		}

		// Tabs (legacy)
		const tabsContainer = $overlay.querySelector(SEL.tabs);
		if (tabsContainer) {
			tabsContainer.addEventListener("click", (e) => {
				const tab = e.target.closest("[data-gv2-tab]");
				if (tab) activateTab(tab.dataset.gv2Tab);
			});
		}

		// Quick-add tab switching (plate ↔ VIN)
		const quickTabs = $overlay.querySelectorAll("[data-gv2-quick-tab]");
		quickTabs.forEach(function (tab) {
			tab.addEventListener("click", function () {
				quickTabs.forEach(function (t) {
					const isTarget = t === tab;
					t.classList.toggle("active", isTarget);
					const text = t.textContent.trim();
					t.innerHTML = isTarget ? "<strong>" + text + "</strong>" : text;
				});
				const quickPanels = $overlay.querySelectorAll("[data-gv2-quick-panel]");
				quickPanels.forEach(function (p) {
					const show = p.dataset.gv2QuickPanel === tab.dataset.gv2QuickTab;
					p.classList.toggle("active", show);
					p.style.display = show ? "" : "none";
				});

				if (tab.dataset.gv2QuickTab === "vin") {
					if ($vinError) $vinError.textContent = "";
					window.igEvents = window.igEvents || [];
					window.igEvents.push({ event: "add_scan_vin_clicked" });
				}
			});
		});

		// Manual search button
		if ($manualSearch) {
			$manualSearch.addEventListener("click", handleManualSearch);
		}
		if ($nativeYmmSlot) {
			$nativeYmmSlot.addEventListener("change", function () {
				queueDraftFitmentPreview();
			});
		}

		// VIN
		if ($vinInput) {
			$vinInput.addEventListener("input", handleVinInputValidation);
		}
		if ($vinSubmit) {
			$vinSubmit.addEventListener("click", handleVinSubmit);
		}

		// License
		if ($licenseState) {
			$licenseState.addEventListener("change", validateLicenseFields);
		}
		if ($licensePlate) {
			$licensePlate.addEventListener("input", validateLicenseFields);
		}
		if ($licenseSubmit) {
			$licenseSubmit.addEventListener("click", handleLicenseSubmit);
		}

		// Browse catalog from preview
		if ($browseCatalog) {
			$browseCatalog.addEventListener("click", () => {
				if (!currentActiveVehicle) return;
				moveVehicleToFront(currentActiveVehicle);
				navigateToGarageVehicleCollection(currentActiveVehicle);
			});
		}

		// Vehicle item click selects it for preview AND makes it the active vehicle
		if ($vehicleList) {
			$vehicleList.addEventListener("click", (e) => {
				const item = e.target.closest(".gv2-vehicle-item");
				if (!item || e.target.closest("[data-gv2-action]")) return;
				if (e.target.closest(".gv2-vehicle-item__meta")) return;
				const idx = parseInt(item.dataset.gv2Vi, 10);
				const terms = getTerms();
				if (terms[idx]) {
					/* Block handleExternalTermsUpdate from re-rendering
					   while this click handler is still running.
					   The flag is cleared after a short delay so that
					   any async listeners that fire from our
					   moveVehicleToFront / syncActiveVehicle events
					   are also suppressed. */
					_internalActionInProgress = true;

					// Highlight
					$vehicleList
						.querySelectorAll(".gv2-vehicle-item")
						.forEach((li) => li.classList.remove("active"));
					item.classList.add("active");
					hydratePreview(terms[idx]);

					// Move to front so homepage hero, header, etc. all update
					moveVehicleToFront(terms[idx]);
					syncActiveVehicle(terms[idx]);

					if (returnToTouchupFinder(terms[idx])) {
						setTimeout(function () {
							_internalActionInProgress = false;
						}, 300);
						return;
					}

					// On collection/search pages, navigate to the new vehicle's collection

					const isCollectionOrSearch =
						(window.location.pathname.startsWith("/collections/") ||
							window.location.pathname.startsWith("/search")) &&
						!window.location.pathname.includes("/products/");
					if (isCollectionOrSearch) {
						navigateToGarageVehicleCollection(terms[idx]);
						return;
					}

					// Re-render with updated order
					// (renderVehicleList auto-marks index 0 as active via the template)
					renderVehicleList(getTerms());

					// Release the guard after external listeners have fired
					setTimeout(function () {
						_internalActionInProgress = false;
					}, 300);
				}
			});
		}
	}

	/* ==================================================================
     VEHICLE LIST CLICK HANDLER
     ================================================================== */
	function handleVehicleListClick(e) {
		const btn = e.target.closest("[data-gv2-action]");
		if (!btn) return;

		// Isolate action button clicks from parent handlers
		e.preventDefault();
		e.stopPropagation();

		const action = btn.dataset.gv2Action;
		const idx = parseInt(btn.dataset.gv2Idx, 10);
		const terms = getTerms();
		const vehicle = terms[idx];
		if (!vehicle) return;

		if (action === "browse" || action === "shop") {
			// Move to front, then navigate
			moveVehicleToFront(vehicle);
			syncActiveVehicle(vehicle);
			navigateToGarageVehicleCollection(vehicle);
		}

		if (action === "edit") {
			editState = { ymm: vehicle.ymm, index: idx };
			_manualDraftRequiresConfirm = false;
			clearFitmentContext();
			window.editCarState = {
				open: true,
				ymm: vehicle.ymm,
				url: "/collections/" + generateCollectionHandle(vehicle.ymm),
			};

			showView("add");
			hydratePreview(vehicle);

			// Hide quick-add section (VIN/License) when editing
			const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
			const orDivider = $overlay.querySelector("[data-garage-or-divider]");
			if (quickAdd) quickAdd.style.display = "none";
			if (orDivider) orDivider.style.display = "none";

			// Prepare manual section and pre-populate YMM fields
			prepareManualView(vehicle);
		}

		if (action === "delete") {
			deleteVehicle(vehicle, idx);
		}
	}

	/* ==================================================================
     DELETE VEHICLE — handles all indices (no page reload)
     ================================================================== */
	function deleteVehicle(vehicle, idx) {
		_internalActionInProgress = true;
		let terms = getTerms();
		const wasFirst = idx === 0;

		// Remove from array
		terms = terms.filter((v) => v.ymm !== vehicle.ymm);

		// Persist — setSearchTerms triggers vh:searchTerms for hero banner
		setSearchTerms(terms, 9);

		// If removing last vehicle, set force-clear flag for EasySearch
		if (terms.length === 0) {
			try {
				sessionStorage.setItem("es_force_clear", "1");
			} catch (e) {}
		}

		// Update fitment object to reflect new first (or empty)
		if (terms.length > 0) {
			const first = terms[0];
			const fields = [
				first.year,
				first.make,
				first.model,
				first.submodel,
				first.engine,
			];
			const fitVal = fields
				.filter((f) => f && f.trim())
				.map((f) => f.trim() + "easysearch-preselect-delimiter")
				.join("");
			try {
				setFitmentObject({
					value: fitVal,
					expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
				});
			} catch (e) {}
		} else {
			try {
				localStorage.setItem(
					"easysearch-preselect-fitment",
					JSON.stringify({ value: "" }),
				);
			} catch (e) {}
		}

		// Update the V2 modal UI instantly
		if (terms.length === 0) {
			showView("garage");
			toggleEmptyState(true);
			editState = null;
			clearPreview();
			syncActiveVehicle(null);
		} else {
			toggleEmptyState(false);
			renderVehicleList(terms);
			hydratePreview(terms[0]);
			syncActiveVehicle(terms[0]);
		}
		// Release the guard after external listeners have fired
		setTimeout(function () {
			_internalActionInProgress = false;
		}, 300);
	}

	/* ==================================================================
     MANUAL SEARCH
     ================================================================== */
	function handleManualSearch() {
		const sel = getManualSelects();
		if (!sel) return;
		const { year, make, model: mdl, submodel, engine } = sel;

		if (!year?.value || !make?.value || !mdl?.value) {
			return;
		}
		if (_manualDraftRequiresConfirm && _fitmentDraftPreviewState === "fail") {
			updateManualSearchDisabledState();
			return;
		}

		const yearVal = year.value;
		const makeVal = make.value;
		const modelVal = mdl.value;
		const submodelVal = submodel?.value || "";
		const engineVal = engine?.value || "";
		const rowFitmentScrollVehicle = _manualDraftRequiresConfirm
			? {
					year: yearVal,
					make: makeVal,
					model: modelVal,
					submodel: submodelVal,
					engine: engineVal,
				}
			: null;
		_manualDraftRequiresConfirm = false;

		let newVin = null;
		let newPaintCode = null;
		let newDecodedByBumper = null;
		let newLastCollectionImage = null;

		// Edit mode — preserve VIN / paint from the vehicle being edited
		if (editState && window.editCarState && window.editCarState.open) {
			const terms = getTerms();
			const idx = terms.findIndex(
				(t) => (t.ymm || "").trim() === window.editCarState.ymm.trim(),
			);
			if (idx >= 0) {
				newVin = terms[idx].vin;
				newPaintCode = terms[idx].paintCode;
				newDecodedByBumper = terms[idx].decodedByBumper;
				newLastCollectionImage = terms[idx].lastCollectionImage;
			}
		}

		if (typeof logGarageEventToSheets === "function") {
			logGarageEventToSheets("dropdown", {});
		}

		// Save vehicle to garage (localStorage + account metafield)
		const ymm = [yearVal, makeVal, modelVal, submodelVal, engineVal]
			.filter(Boolean)
			.join(" ");

		if (editState && window.editCarState && window.editCarState.open) {
			updateSearchTerms({
				ymm: window.editCarState.ymm,
				year: yearVal,
				make: makeVal,
				model: modelVal,
				submodel: submodelVal,
				engine: engineVal,
				vin: newVin,
				paintCode: newPaintCode,
				decodedByBumper: newDecodedByBumper,
				lastCollectionImage: newLastCollectionImage,
				troublesome_format: null,
				troublesome_description: null,
			});

			/* ── Edit-mode save: stay inside modal ──
			   Write fitment, refresh the list, and return
			   without navigating or reloading the page. */
			const fields = [yearVal, makeVal, modelVal, submodelVal, engineVal];
			const fitVal = fields
				.filter(Boolean)
				.map((f) => f + "easysearch-preselect-delimiter")
				.join("");
			setFitmentObject({
				value: fitVal,
				expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
			});

			const updatedTerms = getTerms();
			editState = null;
			window.editCarState = { open: false };

			// Restore quick-add visibility for future add flows
			const quickAdd = $overlay.querySelector("[data-garage-quick-add]");
			const orDivider = $overlay.querySelector("[data-garage-or-divider]");
			if (quickAdd) quickAdd.style.display = "";
			if (orDivider) orDivider.style.display = "";

			if (updatedTerms.length) {
				syncActiveVehicle(updatedTerms[0]);
			}

			if (returnToTouchupFinder(updatedTerms[0])) {
				return;
			}

			if (window.location.pathname.includes("/products/")) {
				close();
				return;
			}

			showView("garage", updatedTerms);
			renderVehicleList(updatedTerms);
			toggleEmptyState(!updatedTerms.length);
			if (updatedTerms.length) {
				hydratePreview(updatedTerms[0]);
			}
			return;
		} else {
			moveVehicleToFront({
				ymm: ymm,
				year: yearVal,
				make: makeVal,
				model: modelVal,
				submodel: submodelVal,
				engine: engineVal,
				vin: newVin,
				paintCode: newPaintCode,
				decodedByBumper: newDecodedByBumper,
				lastCollectionImage: newLastCollectionImage,
				troublesome_format: null,
				troublesome_description: null,
			});
		}

		// Write fitment localStorage so the page reload picks it up
		const fields = [yearVal, makeVal, modelVal, submodelVal, engineVal];
		const fitVal = fields
			.filter(Boolean)
			.map((f) => f + "easysearch-preselect-delimiter")
			.join("");
		setFitmentObject({
			value: fitVal,
			expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
		});

		if (returnToTouchupFinder(getTerms()[0])) {
			return;
		}

		const isProductPage = window.location.pathname.includes("/products/");
		close();
		if (rowFitmentScrollVehicle) {
			scheduleFitmentResultScroll(rowFitmentScrollVehicle);
		}
		if (isProductPage) {
			return;
		}

		// Non-product pages: navigate to collection
		navigateToGarageVehicleCollection(getTerms()[0]);
	}

	/* ==================================================================
     DECODE → NO-REFRESH BRIDGE (PDP)
     ================================================================== */

	/**
	 * After a VIN / license-plate decode succeeds on a product page,
	 * update the page WITHOUT a full reload.
	 *
	 * If the decoded vehicle is missing a submodel and the current product
	 * requires one, the garage stays open with the manual-dropdown view
	 * pre-filled and the submodel field focused.  Otherwise the garage
	 * closes and event-driven listeners handle the page update.
	 */
	function handlePostDecodeOnPDP() {
		const terms = getTerms();
		if (!terms.length) return;

		const vehicle = terms[0];

		const hasSubmodel = !!(vehicle.submodel && String(vehicle.submodel).trim());

		if (window.partifySubmodelRequired && !hasSubmodel) {
			if (_externalSyncDebounce) clearTimeout(_externalSyncDebounce);
			_doSubmodelFocusFlow();
			syncActiveVehicle(vehicle);
			return;
		}

		syncActiveVehicle(vehicle);
		close();
	}

	/* ==================================================================
     VIN HANDLING
     ================================================================== */
	function resetVinForm() {
		if ($vinInput) $vinInput.value = "";
		if ($vinSubmit) $vinSubmit.disabled = true;
		if ($vinError) $vinError.textContent = "";
	}

	function handleVinInputValidation() {
		const val = ($vinInput.value || "")
			.trim()
			.toUpperCase()
			.replace(/[^A-HJ-NPR-Z0-9]/g, "");
		$vinInput.value = val;
		$vinSubmit.disabled = val.length !== 17;
		$vinError.textContent = "";
	}

	async function handleVinSubmit() {
		document.dispatchEvent(new CustomEvent("add_by_vin_clicked"));
		const vin = ($vinInput.value || "").trim().toUpperCase();
		if (vin.length !== 17) {
			$vinError.textContent = i18n("exact17");
			return;
		}

		// Loading state
		$vinSubmit.disabled = true;
		$vinSubmit.classList.add("gv2-btn-loading");
		/* Keep textContent so button retains its intrinsic width;
		   gv2-btn-loading hides label via color:transparent */
		$vinError.textContent = "";

		try {
			// Track attempts
			if (typeof attemptedVinsInGarage !== "undefined")
				attemptedVinsInGarage.push(vin);

			const result = await handleVinDecode(
				vin,
				"",
				"",
				1,
				bumperdotcommake,
				i18n("useYMMDropdown"),
				i18n("noResults"),
				i18n("search"),
				"",
				"",
				"",
				"",
				"",
				"",
				"",
			);

			// Only navigate if the decode actually succeeded
			if (!result) {
				$vinError.textContent = i18n("failedVin");
				return;
			}

			$vinError.textContent = "";
			$vinInput.value = "";
			document.dispatchEvent(new CustomEvent("vin_scanner_success"));

			const terms = getTerms();
			if (returnToTouchupFinder(terms[0])) {
				return;
			}

			const isProductPage = window.location.pathname.includes("/products/");
			if (isProductPage) {
				handlePostDecodeOnPDP();
				return;
			}
			if (terms.length > 0 && terms[0].ymm) {
				navigateToGarageVehicleCollection(terms[0]);
				return;
			}
		} catch (err) {
			$vinError.textContent = i18n("failedVin");
		} finally {
			$vinSubmit.disabled = ($vinInput.value || "").trim().length !== 17;
			$vinSubmit.classList.remove("gv2-btn-loading");
			$vinSubmit.textContent = "ADD VEHICLE";
		}
	}

	/* ==================================================================
     LICENSE PLATE HANDLING
     ================================================================== */
	function validateLicenseFields() {
		if (!$licenseState || !$licensePlate || !$licenseSubmit) return;
		const st = $licenseState.value;
		const raw = ($licensePlate.value || "").toUpperCase();
		if ($licensePlate.value !== raw) $licensePlate.value = raw;
		const pl = raw.trim();
		$licenseSubmit.disabled = !(st && pl.length >= 2);
		if ($licenseError) $licenseError.textContent = "";
	}

	async function handleLicenseSubmit() {
		const state = $licenseState.value;
		const rawPlate = ($licensePlate.value || "").trim();
		if (!state || !rawPlate) return;

		const oRestricted = (cfg().oRestrictedStates || "")
			.split(",")
			.map((s) => s.trim());
		const plate =
			typeof cleanLicensePlate === "function"
				? cleanLicensePlate(state, rawPlate, oRestricted)
				: rawPlate;

		// Check already attempted
		if (
			typeof attemptedDecodedLicensePlates !== "undefined" &&
			attemptedDecodedLicensePlates.includes(state + "-" + plate)
		) {
			$licenseError.textContent = i18n("licensePlateAlreadyAttempted");
			return;
		}

		$licenseSubmit.disabled = true;
		$licenseSubmit.classList.add("gv2-btn-loading");
		/* Keep textContent so button retains its intrinsic width */
		$licenseError.textContent = "";

		try {
			if (typeof attemptedDecodedLicensePlates !== "undefined")
				attemptedDecodedLicensePlates.push(state + "-" + plate);

			const data = await fetchBumpersLicenseToVin(state, plate);
			if (!data || !data.vin) {
				$licenseError.textContent = i18n("failedLicense");
				return;
			}

			// Check bumper make
			const currentMake = (data.make || "").toLowerCase();
			bumperdotcommake = false;
			if (
				cfg().displayLicensePlate &&
				(cfg().bumperMakes || "").toLowerCase().includes(currentMake)
			) {
				bumperdotcommake = true;
			}

			// Decode the VIN
			const licenseResult = await handleVinDecode(
				data.vin,
				state,
				plate,
				1,
				bumperdotcommake,
				i18n("useYMMDropdown"),
				i18n("failedLicense"),
				i18n("search"),
				"",
				"",
				"",
				"",
				"",
				"",
				"",
			);

			// Only navigate if the decode actually succeeded
			if (!licenseResult) return;

			const terms = getTerms();
			if (returnToTouchupFinder(terms[0])) {
				return;
			}

			const isProductPage = window.location.pathname.includes("/products/");
			if (isProductPage) {
				handlePostDecodeOnPDP();
				return;
			}
			if (terms.length > 0 && terms[0].ymm) {
				navigateToGarageVehicleCollection(terms[0]);
				return;
			}
		} catch (err) {
			$licenseError.textContent = err.message.includes("already been attempted")
				? i18n("licensePlateAlreadyAttempted")
				: i18n("failedLicense");
		} finally {
			$licenseSubmit.disabled = false;
			$licenseSubmit.classList.remove("gv2-btn-loading");
			$licenseSubmit.textContent = "ADD VEHICLE";
			$licensePlate.value = "";
			if ($licenseState) $licenseState.selectedIndex = 0;
		}
	}

	/* ==================================================================
     HELPERS
     ================================================================== */
	function getTerms() {
		try {
			return typeof getSearchTerms === "function"
				? getSearchTerms()
				: JSON.parse(localStorage.getItem("searchTerms") || "[]");
		} catch (e) {
			return [];
		}
	}

	/**
	 * Sync the active vehicle to header, homepage hero bar, and fitment.
	 * Pass null when no vehicle is active (after clearing garage).
	 *
	 * VEHICLE STATE BRIDGE (v2)
	 * ─────────────────────────
	 * Canonical event:  "partify:vehicle:changed" (window, detail = vehicle|null)
	 * Compat signals:   "vh:searchTerms" (legacy hero banner)
	 * Canonical key:    localStorage "searchTerms" (unchanged, written by setSearchTerms/moveVehicleToFront)
	 * Vehicle shape:    { year, make, model, submodel?, engine?, vin?, paintCode?, ymm }
	 *                   vin is always UPPERCASE when present.
	 *
	 * NOTE: We no longer dispatch searchTermsUpdated from here because every
	 * upstream caller (moveVehicleToFront, setSearchTerms) already dispatches it.
	 * Dispatching it AGAIN from syncActiveVehicle was the #1 event amplifier.
	 */
	let _lastSyncedVehicleId = null;
	function syncActiveVehicle(vehicle) {
		/* ── Normalize VIN uppercase ── */
		if (vehicle && vehicle.vin) {
			vehicle.vin = String(vehicle.vin).toUpperCase();
		}

		/* ── Same-vehicle short-circuit ── */
		const batcher = window.__partifyVehicleChangeBatcher;
		const currentId = batcher
			? batcher.normalizeVehicleId(vehicle)
			: JSON.stringify(vehicle);
		const isSameVehicle = currentId === _lastSyncedVehicleId;
		_lastSyncedVehicleId = currentId;

		if (vehicle) {
			const ymm = vehicle.ymm || "";

			// 1. Header garage text (desktop + mobile)
			updateHeaderVehicle(ymm);

			// 2. Homepage garage bar title
			const garageBarTitle = document.getElementById("garage-vehicle-name");
			if (garageBarTitle) garageBarTitle.textContent = ymm;

			// 3. Fitment object — so hero banner and PDP fitment pick up the change
			const fields = [
				vehicle.year,
				vehicle.make,
				vehicle.model,
				vehicle.submodel,
				vehicle.engine,
			];
			const fitVal = fields
				.filter((f) => f && String(f).trim())
				.map((f) => String(f).trim() + "easysearch-preselect-delimiter")
				.join("");
			try {
				setFitmentObject({
					value: fitVal,
					expires: String(Date.now() + 365 * 24 * 60 * 60 * 1000),
				});
			} catch (e) {}

			// 4. Fire legacy hero banner event (only on actual change)
			if (!isSameVehicle) {
				window.dispatchEvent(new Event("vh:searchTerms"));
			}
		} else {
			// No vehicle — reset header
			updateHeaderVehicle("");

			const garageBarTitle = document.getElementById("garage-vehicle-name");
			if (garageBarTitle) garageBarTitle.textContent = "Select Your Vehicle";

			try {
				localStorage.setItem(
					"easysearch-preselect-fitment",
					JSON.stringify({ value: "" }),
				);
			} catch (e) {}

			if (!isSameVehicle) {
				window.dispatchEvent(new Event("vh:searchTerms"));
			}
		}

		/* ── Canonical vehicle-changed event (v2 bridge) ──
		   Skip if vehicle identity hasn't changed (prevents re-entrant storms). */
		if (!isSameVehicle) {
			const payload = vehicle
				? {
						year: vehicle.year || "",
						make: vehicle.make || "",
						model: vehicle.model || "",
						submodel: vehicle.submodel || "",
						engine: vehicle.engine || "",
						vin: vehicle.vin || null,
						paintCode: vehicle.paintCode || null,
						ymm: vehicle.ymm || "",
					}
				: null;
			window.dispatchEvent(
				new CustomEvent("partify:vehicle:changed", { detail: payload }),
			);

			/* Feed the shared batcher so all registered consumers fire once */
			if (batcher) batcher.schedule(vehicle, "syncActiveVehicle");
		}
	}

	function escHtml(s) {
		const d = document.createElement("div");
		d.textContent = s;
		return d.innerHTML;
	}

	function escAttr(s) {
		return String(s || "")
			.replace(/"/g, "&quot;")
			.replace(/</g, "&lt;");
	}

	function wait(ms) {
		return new Promise((r) => setTimeout(r, ms));
	}

	function updateHeaderVehicle(ymm) {
		if (window.GarageUI && window.GarageUI.updateVehicleSelectionElement) {
			window.GarageUI.updateVehicleSelectionElement(ymm);
		}
	}

	/* ==================================================================
     HEADER GARAGE BUTTON BINDING
     Ensures both #easyBtn (desktop) and #easyBtnMobile (mobile)
     open the V2 modal, and keeps both garages in sync.
     ================================================================== */
	function bindHeaderGarageButtons() {
		const easyBtn = document.getElementById("easyBtn");
		if (easyBtn && !easyBtn.dataset.gv2Bound) {
			easyBtn.dataset.gv2Bound = "true";
			easyBtn.addEventListener("click", function (e) {
				e.preventDefault();
				e.stopPropagation();
				toggle();
			});
		}
		// Also bind any elements with data-open-garage attribute
		document.querySelectorAll("[data-open-garage]").forEach(function (el) {
			if (el.dataset.gv2Bound) return;
			el.dataset.gv2Bound = "true";
			el.addEventListener("click", function (e) {
				e.preventDefault();
				open();
			});
		});
	}

	/* ==================================================================
     EXTERNAL SYNC — when something outside V2 changes searchTerms
     (e.g. hero banner, old popup remnant, direct localStorage edit)
     ================================================================== */
	let _externalSyncDebounce = null;
	let _internalActionInProgress = false;
	function handleExternalTermsUpdate() {
		/* If the garage modal itself just dispatched events (via click
		   handler → moveVehicleToFront → setSearchTerms), skip the
		   re-render — the click handler already updated the UI. */
		if (_internalActionInProgress) return;

		/* Skip while editing — dropdown changes fire searchTermsUpdated
		   and we must not snap back to garage view mid-edit. */
		if (editState) return;

		if (_externalSyncDebounce) clearTimeout(_externalSyncDebounce);
		_externalSyncDebounce = setTimeout(function () {
			const terms = getTerms();
			// Update header text
			if (terms.length) {
				updateHeaderVehicle(terms[0].ymm || "");
			} else {
				updateHeaderVehicle("");
			}
			// If V2 modal is open, refresh its view
			if (isOpen) {
				if (terms.length) {
					showView("garage", terms);
					renderVehicleList(terms);
					hydratePreview(terms[0]);
					toggleEmptyState(false);
				} else {
					showView("garage");
					toggleEmptyState(true);
					clearPreview();
				}
			}
		}, 150);
	}

	/* ==================================================================
     BOOTSTRAP
     ================================================================== */
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
